import React, { useEffect, useState, useMemo, forwardRef, useCallback, useRef } from 'react';
import { Icon } from '@iconify/react';
import Dropdown from 'bootstrap/js/dist/dropdown';
import {
  getTasksByAsset,
  deleteTasks,
  updateTasks,
  createTasks,
  copyTasksFromAsset
} from '../../api/tasksAPI';
import { fitRotableToAsset, getRotables } from '../../api/rotablesAPI';
import { useFormBuilder } from '../../utils/useFormBuilder';
import { validateForm, renderErrorDot, useValidator } from '../../utils/useValidator';
import { formatIntDate } from "../../utils/dateUtils.js";
import { useUserProfile } from '../../masterLayout/MasterLayoutBody';
import { useSetting } from '../../utils/AppSettingsContext';
import { useModule } from '../../utils/useModule';
import { fetchData } from '../../utils/apiHelpers';
import { clearCache } from '../../utils/lookupCache';
import { useTableFilters } from '../../utils/useTableFilters';
import Select from 'react-select';
import { createParts, getParts } from '../../api/partsAPI';
import { getManufacturer } from '../../api/manufacturerAPI';
import { getSuppliers } from '../../api/supplierAPI';
import { getTrades } from '../../api/tradesAPI';
import { getExpenseCodes } from '../../api/expense-codeAPI';
import { getExternalServices } from '../../api/external-servicesAPI';
import { getSchedulingGroups } from '../../api/scheduling-groupAPI';
import {
  getBillOfMaterialsByTaskId,
  createBillOfMaterials,
  updateBillOfMaterials,
  deleteBillOfMaterials
} from '../../api/bill-of-materialsAPI';
import {
  getBillOfEquipmentByTaskId,
  createBillOfEquipment,
  updateBillOfEquipment,
  deleteBillOfEquipment
} from '../../api/bill-of-equipmentAPI';
import {
  getBillOfLabourByTaskId,
  createBillOfLabour,
  updateBillOfLabour,
  deleteBillOfLabour
} from '../../api/bill-of-labourAPI';
import {
  getBillOfServicesByTaskId,
  createBillOfServices,
  updateBillOfServices,
  deleteBillOfServices
} from '../../api/bill-of-servicesAPI';
import { getPaginatedMaintenancePlans } from '../../api/maintenance-planAPI';
import {
  getBillOfMaterialsMpsByMaintenancePlanId,
} from '../../api/bill-of-materials-mpAPI';
import {
  getBillOfEquipmentMpsByMaintenancePlanId,
} from '../../api/bill-of-equipment-mpAPI';
import {
  getBillOfLabourMpsByMaintenancePlanId,
} from '../../api/bill-of-labour-mpAPI';
import {
  getBillOfServicesMpsByMaintenancePlanId,
} from '../../api/bill-of-services-mpAPI';
import { getAssetMeter } from '../../api/asset-meterAPI';
import { estimateMeterReading } from '../../api/meter-readingsAPI';
import { deleteUploadedFile } from '../../api/uploadAPI';
import DocumentDataTable from '../DocumentDataTable';
import TaskImporter from '../DataImporter/TaskImporter';
import { getPaginatedAssets } from '../../api/assetAPI';
import { showToastError, showToastSuccess } from '../../utils/errorController';
import { getTaskCycleGroupById } from '../../api/task-cycle-groupAPI';
import CycleTasksEditor from './CycleTasksEditor';
import { CURRENT_TASK_PLACEHOLDER, createCycleRow, resolveAssetMeterId } from './taskCycleUtils';
import { getTaskFormFields, getDynamicTaskFormFields } from './taskFieldConfigs';
import AddPartModal from '../parts/AddPartModal';
import AddPartsModal from '../parts/AddPartsModal';
import WorkOrderDetails from '../WorkOrderDetails';
import TaskOccurrencesModal from '../TaskOccurrencesModal';
import { getWorkOrderById } from '../../api/work-orderAPI';
import { getWorkOrderPartsByWorkOrderId } from '../../api/work-order-partsAPI';
import { getBillOfEquipmentWosByWorkOrderId } from '../../api/bill-of-equipment-woAPI';
import { getBillOfLabourWosByWorkOrderId } from '../../api/bill-of-labour-woAPI';
import { getBillOfServicesWosByWorkOrderId } from '../../api/bill-of-services-woAPI';
import { getWorkOrderFormsByWorkOrderId } from '../../api/work-order-formsAPI';
import { getWorkOrderStatus } from '../../api/work-order-statusAPI';
import { createBillOfMaterialsWithAi } from '../../api/ai-bill-of-materialsAPI';
import { getForms, getFormById } from '../../api/formsAPI';
import { getFormInputType } from '../../api/form-input-typeAPI';
import FormLivePreview from '../FormLivePreview';
import { renderPreviewInput as previewRenderer } from '../../utils/renderPreviewInput';

const normalizeFileKey = (value) => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  try {
    const url = new URL(trimmed);
    return url.pathname.replace(/^\//, '');
  } catch (err) {
    return trimmed.replace(/^\/+/, '');
  }
};

const extractDeletionTargets = (fileEntry, fallback) => {
  const candidates = [];
  if (fileEntry) {
    if (typeof fileEntry === 'string') {
      candidates.push(fileEntry);
    } else if (typeof fileEntry === 'object') {
      const { key, filename, name, url } = fileEntry;
      if (typeof key === 'string') candidates.push(key);
      if (typeof filename === 'string') candidates.push(filename);
      if (typeof name === 'string') candidates.push(name);
      if (typeof url === 'string') candidates.push(url);
    }
  }
  if (typeof fallback === 'string') {
    candidates.push(fallback);
  }

  const firstValid = candidates.find((item) => typeof item === 'string' && item.trim());
  const normalizedKey = normalizeFileKey(firstValid);
  const url =
    fileEntry && typeof fileEntry === 'object' && typeof fileEntry.url === 'string'
      ? fileEntry.url.trim()
      : undefined;

  return { key: normalizedKey, url };
};

const normalizeRotableGroups = (groups) => {
  if (groups === null || groups === undefined) return [];
  const coerceGroupId = (group) => {
    if (group === null || group === undefined) return null;
    if (typeof group === 'object') {
      return group.id ?? group.groupId ?? group.value ?? null;
    }
    return group;
  };
  if (Array.isArray(groups)) {
    return groups
      .map(coerceGroupId)
      .filter((value) => value !== null && value !== undefined && String(value).trim() !== '')
      .map((value) => (typeof value === 'string' ? value.trim() : value));
  }
  if (typeof groups === 'string') {
    const trimmed = groups.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      return normalizeRotableGroups(parsed);
    } catch (err) {
      return [];
    }
  }
  if (typeof groups === 'object') {
    if (Array.isArray(groups.groups)) return normalizeRotableGroups(groups.groups);
    if (Array.isArray(groups.group)) return normalizeRotableGroups(groups.group);
  }
  return [];
};

const removeMatchingUrls = (urls, key, url) => {
  if (!Array.isArray(urls)) return urls;
  if (!key && !url) return urls;
  let modified = false;
  const filtered = urls.filter((item) => {
    if (typeof item !== 'string') return true;
    const trimmed = item.trim();
    if (url && trimmed === url) {
      modified = true;
      return false;
    }
    if (key && normalizeFileKey(trimmed) === key) {
      modified = true;
      return false;
    }
    return true;
  });

  return modified ? filtered : urls;
};

const toTrimmedString = (value) => (typeof value === 'string' ? value.trim() : '');

const normalizeRotablesIdValue = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  return value;
};

const getTaskCodeValue = (task) =>
  toTrimmedString(task?.code?.code) ||
  toTrimmedString(task?.taskCode?.code) ||
  toTrimmedString(task?.code?.name) ||
  toTrimmedString(task?.taskCode?.name);

const parseTaskCodeSortParts = (value) => {
  const trimmed = toTrimmedString(value);
  if (!trimmed) {
    return { number: Number.POSITIVE_INFINITY, suffix: '', isNumeric: false };
  }
  const match = trimmed.match(/^(\d+)(.*)$/);
  if (!match) {
    return { number: Number.POSITIVE_INFINITY, suffix: trimmed.toLowerCase(), isNumeric: false };
  }
  return {
    number: Number(match[1]),
    suffix: match[2]?.toLowerCase() || '',
    isNumeric: true,
  };
};

const stickyHeaderCellStyle = {
  position: 'sticky',
  top: 0,
  zIndex: 3,
  backgroundColor: 'var(--bs-body-bg)',
  boxShadow: 'inset 0 -1px 0 var(--bs-border-color)',
};

const AssetTasks = forwardRef(({ assetId, asset }, ref) => {
  const [tasks, setTasks] = useState([]);
  const [isTasksLoading, setIsTasksLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editTaskId, setEditTaskId] = useState(null);
  const [editRow, setEditRow] = useState({});
  const userProfile = useUserProfile();
  const defaultExpenseParts = useSetting('default_expense_parts');
  const defaultExpenseLabour = useSetting('default_expense_labour');
  const defaultExpenseMisc = useSetting('default_expense_misc');
  const defaultExpenseServices = useSetting('default_expense_services');
  const showTaskRefSource = useSetting('show_task_ref_source');
  const useCostResponsibility = useSetting('use_cost_responsibility');
  const useRotables = useModule('rotables');
  const [, setTaskTypes] = useState([]);
  const [, setUnits] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalTask, setModalTask] = useState(null);
  const [showAlignScheduleModal, setShowAlignScheduleModal] = useState(false);
  const [alignScheduleSelection, setAlignScheduleSelection] = useState([]);
  const [alignScheduleInputs, setAlignScheduleInputs] = useState({});
  const [alignScheduleDirty, setAlignScheduleDirty] = useState({});
  const [alignScheduleSaving, setAlignScheduleSaving] = useState(false);
  const [rotables, setRotables] = useState([]);
  const [rotablesLoading, setRotablesLoading] = useState(false);
  const isNewModalTask = modalTask?.id === undefined;
  const initialRotablesIdRef = useRef(null);
  const [taskMaterials, setTaskMaterials] = useState([]);
  const [deletedMaterialIds, setDeletedMaterialIds] = useState([]);
  const [taskEquipment, setTaskEquipment] = useState([]);
  const [deletedEquipmentIds, setDeletedEquipmentIds] = useState([]);
  const [taskLabour, setTaskLabour] = useState([]);
  const [deletedLabourIds, setDeletedLabourIds] = useState([]);
  const [taskServices, setTaskServices] = useState([]);
  const [deletedServiceIds, setDeletedServiceIds] = useState([]);
  const [partsOverride, setPartsOverride] = useState(false);
  const [labourOverride, setLabourOverride] = useState(false);
  const [servicesOverride, setServicesOverride] = useState(false);
  const [tradeOptions, setTradeOptions] = useState([]);
  const [expenseOptions, setExpenseOptions] = useState([]);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [schedulingGroupOptions, setSchedulingGroupOptions] = useState([]);
  const [taskCodeOptions,] = useState([]);
  const [lookupRefreshKey, setLookupRefreshKey] = useState(0);
  const [isLastDoneLocked, setIsLastDoneLocked] = useState(true);
  const [showLastDoneWarning, setShowLastDoneWarning] = useState(false);
  const [latestMeterReading, setLatestMeterReading] = useState(null);
  const [selectedTaskCodeName, setSelectedTaskCodeName] = useState('');
  const [cycleForm, setCycleForm] = useState({ name: '' });
  const [cycleRows, setCycleRows] = useState([]);
  const [cycleRowErrors, setCycleRowErrors] = useState({});
  const [showSchedulingTypeConfirm, setShowSchedulingTypeConfirm] = useState(false);
  const [pendingSchedulingType, setPendingSchedulingType] = useState(null);
  const [isCycleLoading, setIsCycleLoading] = useState(false);
  const defaultMeterAppliedRef = useRef(false);
  const lastDoneEditedRef = useRef(false);
  const initialLastDoneRef = useRef('');
  const [totalLcc, setTotalLcc] = useState(0);
  const [totalLccPerUnit, setTotalLccPerUnit] = useState(0);
  const [measurementUnits, setMeasurementUnits] = useState(null);
  const [workOrderStatuses, setWorkOrderStatuses] = useState([]);

  useEffect(() => {
    if (!showModal) {
      return;
    }

    clearCache('task-codes');
    clearCache('modifier-codes');
    setLookupRefreshKey((prev) => prev + 1);
  }, [showModal]);

  useEffect(() => {
    if (!showModal || !useRotables) {
      return;
    }

    let isActive = true;
    setRotablesLoading(true);

    getRotables()
      .then((data) => {
        if (!isActive) return;
        setRotables(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (!isActive) return;
        console.error('Failed to load rotables', err);
        setRotables([]);
      })
      .finally(() => {
        if (isActive) {
          setRotablesLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [showModal, useRotables]);

  const assetGroupKey = useMemo(() => {
    const rawGroup = asset?.groupId ?? asset?.group?.id ?? null;
    return rawGroup !== null && rawGroup !== undefined ? String(rawGroup) : null;
  }, [asset?.groupId, asset?.group?.id]);

  const filteredRotables = useMemo(() => {
    if (!useRotables) return [];

    return rotables.filter((rotable) => {
      if (Number(rotable?.statusId) !== 1) return false;
      const groupIds = normalizeRotableGroups(rotable?.groups)
        .map((group) => (group === null || group === undefined ? '' : String(group).trim()))
        .filter(Boolean);

      if (!assetGroupKey) return false;
      return groupIds.includes(assetGroupKey);
    });
  }, [assetGroupKey, rotables, useRotables]);

  const rotableOptions = useMemo(
    () =>
      filteredRotables.map((rotable) => {
        const partDescription = rotable?.part?.description || rotable?.partDescription || '';
        const partNumber = rotable?.part?.partNumber ?? rotable?.partNumber ?? '';
        const detailParts = [
          rotable?.serialNumber ? String(rotable.serialNumber).trim() : '',
          partDescription ? `${partDescription}${partNumber ? ` (${partNumber})` : ''}` : '',
        ].filter(Boolean);
        return {
          value: rotable.id,
          label: detailParts.join(' - ') || `Rotable ${rotable.id}`,
        };
      }),
    [filteredRotables]
  );

  const normalizedRotablesId = useMemo(
    () => normalizeRotablesIdValue(modalTask?.rotablesId),
    [modalTask?.rotablesId]
  );

  const selectedRotableOption = useMemo(() => {
    if (!normalizedRotablesId) return null;
    const matched = rotableOptions.find(
      (option) => Number(option.value) === Number(normalizedRotablesId)
    );
    if (matched) return matched;
    const fallbackSerial = toTrimmedString(modalTask?.rotable?.serialNumber);
    const fallbackDescription = toTrimmedString(modalTask?.rotable?.part?.description);
    const fallbackNumber = toTrimmedString(modalTask?.rotable?.part?.partNumber);
    const fallbackLabel = [fallbackSerial, fallbackDescription]
      .filter(Boolean)
      .join(' - ');
    const labelWithNumber =
      fallbackLabel && fallbackNumber ? `${fallbackLabel} (${fallbackNumber})` : fallbackLabel;
    return {
      value: normalizedRotablesId,
      label: labelWithNumber || `Rotable ${normalizedRotablesId}`,
    };
  }, [modalTask?.rotable, normalizedRotablesId, rotableOptions]);

  const selectedRotable = useMemo(() => {
    if (!normalizedRotablesId) return null;
    const id = Number(normalizedRotablesId);
    if (!Number.isFinite(id)) return null;
    const matched = filteredRotables.find((rotable) => Number(rotable.id) === id);
    if (matched) return matched;
    const rotableFromTask = modalTask?.rotable;
    if (!rotableFromTask) return null;
    const rotableId = Number(rotableFromTask.id ?? rotableFromTask.rotableId ?? id);
    return Number.isFinite(rotableId) && rotableId === id ? rotableFromTask : null;
  }, [filteredRotables, modalTask?.rotable, normalizedRotablesId]);

  const formatCodeWithName = useCallback((codeValue, nameValue) => {
    const codePart = codeValue !== undefined && codeValue !== null ? String(codeValue).trim() : '';
    const namePart = nameValue !== undefined && nameValue !== null ? String(nameValue).trim() : '';

    if (codePart && namePart) {
      return `${codePart} - ${namePart}`;
    }

    return codePart || namePart || '';
  }, []);
  const getCodeOptionDisplay = useCallback(
    (option) => {
      if (!option) return '';

      const codePart =
        option.code !== undefined && option.code !== null
          ? String(option.code).trim()
          : undefined;
      const namePart =
        option.name !== undefined && option.name !== null
          ? String(option.name).trim()
          : undefined;

      const combined = formatCodeWithName(codePart, namePart);
      if (combined) {
        return combined;
      }

      if (typeof option.label === 'string' && option.label.trim()) {
        return option.label.trim();
      }

      if (option.value !== undefined && option.value !== null) {
        return String(option.value);
      }

      return '';
    },
    [formatCodeWithName],
  );

  const renderCodeOptionLabel = useCallback(
    (option) => <span>{getCodeOptionDisplay(option)}</span>,
    [getCodeOptionDisplay],
  );
  const [assetMeters, setAssetMeters] = useState([]);
  const [showPartModal, setShowPartModal] = useState(false);
  const [showBomAiModal, setShowBomAiModal] = useState(false);
  const [bomAiText, setBomAiText] = useState('');
  const [bomAiError, setBomAiError] = useState('');
  const [bomAiLoading, setBomAiLoading] = useState(false);
  const [missingBomParts, setMissingBomParts] = useState([]);
  const [showMissingBomModal, setShowMissingBomModal] = useState(false);
  const [missingBomError, setMissingBomError] = useState('');
  const [missingBomSaving, setMissingBomSaving] = useState(false);
  const [bomManufacturerOptions, setBomManufacturerOptions] = useState([]);
  const [bomSupplierOptions, setBomSupplierOptions] = useState([]);
  const [bomApplySourceToAll, setBomApplySourceToAll] = useState(false);
  const [bomApplySupplierToAll, setBomApplySupplierToAll] = useState(false);
  const [bomGlobalSourceId, setBomGlobalSourceId] = useState('');
  const [bomGlobalSupplierId, setBomGlobalSupplierId] = useState('');
  const [editingPartIndex, setEditingPartIndex] = useState(null);
  const [partForm, setPartForm] = useState({});
  const [selectedPartOption, setSelectedPartOption] = useState(null);
  const [, setInitialPartOptions] = useState([]);
  const [showCreatePartModal, setShowCreatePartModal] = useState(false);
  const [addPartInitialValues, setAddPartInitialValues] = useState({});
  const [partSearchText, setPartSearchText] = useState('');
  const [partSelectRefreshKey, setPartSelectRefreshKey] = useState(0);
  const [showEquipmentModal, setShowEquipmentModal] = useState(false);
  const [editingEquipmentIndex, setEditingEquipmentIndex] = useState(null);
  const [equipmentForm, setEquipmentForm] = useState({});
  const [showLabourModal, setShowLabourModal] = useState(false);
  const [editingLabourIndex, setEditingLabourIndex] = useState(null);
  const [labourForm, setLabourForm] = useState({});
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateTask, setTemplateTask] = useState({});
  const [templateErrors, setTemplateErrors] = useState({});
  const [showTaskImporter, setShowTaskImporter] = useState(false);
  const [partModalErrors, setPartModalErrors] = useState({});
  const [labourModalErrors, setLabourModalErrors] = useState({});
  const [serviceModalErrors, setServiceModalErrors] = useState({});
  const [equipmentModalErrors, setEquipmentModalErrors] = useState({});
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingServiceIndex, setEditingServiceIndex] = useState(null);
  const [serviceForm, setServiceForm] = useState({});
  const [showOccurrencesModal, setShowOccurrencesModal] = useState(false);
  const [taskOccurrences, setTaskOccurrences] = useState([]);
  const [showWorkOrderDetailsModal, setShowWorkOrderDetailsModal] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [workOrderDetails, setWorkOrderDetails] = useState(null);
  const [workOrderParts, setWorkOrderParts] = useState([]);
  const [workOrderLabour, setWorkOrderLabour] = useState([]);
  const [workOrderServices, setWorkOrderServices] = useState([]);
  const [workOrderEquipment, setWorkOrderEquipment] = useState([]);
  const [workOrderForms, setWorkOrderForms] = useState([]);
  const [taskFormOptions, setTaskFormOptions] = useState([]);
  const [showTaskFormModal, setShowTaskFormModal] = useState(false);
  const [selectedTaskFormId, setSelectedTaskFormId] = useState('');
  const [showTaskFormPreviewModal, setShowTaskFormPreviewModal] = useState(false);
  const [taskFormPreviewLoading, setTaskFormPreviewLoading] = useState(false);
  const [previewTaskForm, setPreviewTaskForm] = useState(null);
  const [taskFormInputTypes, setTaskFormInputTypes] = useState([]);
  const [workOrderDetailsLoading, setWorkOrderDetailsLoading] = useState(false);
  const [occCounts, setOccCounts] = useState({});
  const [taskCosts, setTaskCosts] = useState({});
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [showCopyFromAssetModal, setShowCopyFromAssetModal] = useState(false);
  const [copyForm, setCopyForm] = useState({ sourceAssetId: null, deleteExisting: 0, costCentreId: null });
  const [copyErrors, setCopyErrors] = useState({});
  const [copySelectedAssetOption, setCopySelectedAssetOption] = useState(null);
  const [copyPreviewTasks, setCopyPreviewTasks] = useState([]);
  const [copyLoadingTasks, setCopyLoadingTasks] = useState(false);
  const [copyProgress, setCopyProgress] = useState({ status: 'idle', message: '', current: 0, total: 0 });
  const [copyResult, setCopyResult] = useState(null);
  const [copySubmitting, setCopySubmitting] = useState(false);
  const [copyCompleted, setCopyCompleted] = useState(false);
  const [copyTaskSelectionError, setCopyTaskSelectionError] = useState('');

  const toDateInputValue = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const PARTS_PAGE_SIZE = 25;
  const DEFAULT_PART_CATEGORY_ID = 25;

  useEffect(() => {
    if (!showMissingBomModal) return;
    let isMounted = true;

    const loadBomOptions = async () => {
      try {
        const [manufacturers, suppliers] = await Promise.all([
          getManufacturer(),
          getSuppliers({ page: 1, pageSize: 200 }),
        ]);
        if (!isMounted) return;
        const manufacturerList = Array.isArray(manufacturers)
          ? manufacturers
          : Array.isArray(manufacturers?.data)
            ? manufacturers.data
            : [];
        const supplierList = Array.isArray(suppliers?.data)
          ? suppliers.data
          : Array.isArray(suppliers)
            ? suppliers
            : [];
        setBomManufacturerOptions(manufacturerList);
        setBomSupplierOptions(supplierList);
      } catch (error) {
        console.error('Error loading BOM supplier data:', error);
      }
    };

    loadBomOptions();

    return () => {
      isMounted = false;
    };
  }, [showMissingBomModal]);

  const addNewPartOption = useMemo(
    () => ({ value: 'add-new', label: '+ Add New Part', isAddNew: true }),
    [],
  );

  const formatPartOptionLabel = useCallback((partNumber, description) => {
    const safeNumber = typeof partNumber === 'string' || typeof partNumber === 'number'
      ? String(partNumber).trim()
      : '';
    const safeDescription = typeof description === 'string' ? description.trim() : '';
    return [safeNumber, safeDescription].filter(Boolean).join(' – ');
  }, []);

  const handlePartSearch = useCallback(
    async (inputValue = '', { page } = {}) => {
      const raw = typeof inputValue === 'string' ? inputValue : inputValue?.search ?? '';
      const trimmed = raw.trim();
      setPartSearchText(trimmed);
      const pageNumber = typeof page === 'number' && page > 0 ? page : 1;
      const shouldLoadDefault = trimmed === '';

      if (!shouldLoadDefault && trimmed.length < 2) {
        return { options: [addNewPartOption], hasMore: false };
      }

      try {
        const resp = await getParts({
          page: pageNumber,
          pageSize: PARTS_PAGE_SIZE,
          search: shouldLoadDefault ? '' : trimmed,
        });
        const items = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
        const options = items.map((item) => ({
          value: item.id,
          label: formatPartOptionLabel(item.partNumber, item.description) || `Part ${item.id}`,
          raw: item,
        }));
        const optionsWithAddNew = pageNumber === 1 ? [addNewPartOption, ...options] : options;
        const totalCount =
          typeof resp?.total === 'number'
            ? resp.total
            : typeof resp?.count === 'number'
              ? resp.count
              : typeof resp?.meta?.total === 'number'
                ? resp.meta.total
                : undefined;
        const hasMore =
          typeof resp?.hasMore === 'boolean'
            ? resp.hasMore
            : typeof totalCount === 'number'
              ? pageNumber * PARTS_PAGE_SIZE < totalCount
              : items.length === PARTS_PAGE_SIZE;

        return {
          options: optionsWithAddNew,
          totalCount,
          pageSize: PARTS_PAGE_SIZE,
          hasMore,
        };
      } catch (err) {
        console.error('Error fetching parts:', err);
        return { options: [addNewPartOption], hasMore: false };
      }
    },
    [addNewPartOption, formatPartOptionLabel],
  );

  const partModalFields = useMemo(
    () => [
      {
        field: 'partsId',
        label: 'Part',
        type: 'paginated-select',
        size: 12,
        loadOptions: handlePartSearch,
        selectedOption: selectedPartOption,
        setSelectedOption: setSelectedPartOption,
        required: true,
        isClearable: true,
        onChange: (option, setFormData, field, setSelectedOptionFn) => {
          if (option?.isAddNew) {
            if (setSelectedOptionFn) {
              setSelectedOptionFn(null);
            }
            setAddPartInitialValues((prev) => ({
              ...prev,
              partNumber: partSearchText,
            }));
            setShowCreatePartModal(true);
            return;
          }

          if (setSelectedOptionFn) {
            setSelectedOptionFn(option ?? null);
          }

          setFormData((prev) => {
            const next = {
              ...prev,
              [field]: option?.value ?? null,
              partsId: option?.value ?? null,
            };

            if (option && option.raw) {
              next.parts = option.raw;
              next.partNumber = option.raw.partNumber ?? '';
              next.partDescription = option.raw.description ?? '';
              const resolvedPrice =
                option.raw.lastPrice ?? option.raw.partsCost ?? option.raw.partsPrice ?? '';
              const resolvedPricingDate = option.raw.pricingDate
                ? toDateInputValue(option.raw.pricingDate)
                : '';
              next.partsPrice = resolvedPrice;
              next.pricingDate = resolvedPricingDate || prev.pricingDate || '';
            } else if (!option) {
              next.parts = null;
              next.partNumber = '';
              next.partDescription = '';
              next.partsPrice = '';
              next.pricingDate = '';
            }

            return next;
          });
        },
      },
      { field: 'quantity', label: 'Quantity', type: 'number', size: 4, required: true },
      { field: 'partsPrice', label: 'Parts Cost', type: 'number', size: 4 },
      { field: 'optional', label: 'Optional?', type: 'switch', size: 4 },
      { field: 'pricingDate', label: 'Pricing Date', type: 'date', size: 6, required: true },
      {
        field: 'defaultExpenseCode',
        label: 'Expense Code',
        type: 'select',
        size: 6,
        options: expenseOptions.map((ec) => ({ value: ec.id, label: ec.name })),
        required: true,
      },
    ],
    [expenseOptions, handlePartSearch, partSearchText, selectedPartOption],
  );

  const { renderField: renderPartModalField } = useFormBuilder(
    partModalFields,
    partForm,
    setPartForm,
    partModalErrors,
    { refreshKey: partSelectRefreshKey },
  );

  const labourModalFields = useMemo(
    () => [
      {
        field: 'tradeId',
        label: 'Trade',
        type: 'select',
        size: 6,
        options: tradeOptions.map((t) => ({ value: t.id, label: t.name })),
        required: true,
      },
      { field: 'qty', label: 'Quantity', type: 'number', size: 6, required: true },
      { field: 'labourHours', label: 'Hours', type: 'number', size: 6, required: true },
      {
        field: 'defaultExpenseCode',
        label: 'Expense Code',
        type: 'select',
        size: 6,
        options: expenseOptions.map((ec) => ({ value: ec.id, label: ec.name })),
        required: true,
      },
      { field: 'labourRate', label: 'Labour Rate', type: 'number', size: 6, required: true },
      { field: 'notes', label: 'Notes', type: 'textarea', size: 12 },
    ],
    [tradeOptions, expenseOptions],
  );

  const { renderField: renderLabourModalField } = useFormBuilder(
    labourModalFields,
    labourForm,
    setLabourForm,
    labourModalErrors,
  );

  const serviceModalFields = useMemo(
    () => [
      {
        field: 'serviceId',
        label: 'Service',
        type: 'select',
        size: 6,
        options: serviceOptions.map((s) => ({ value: s.id, label: s.name })),
        required: true,
      },
      { field: 'quantity', label: 'Quantity', type: 'number', size: 6, required: true },
      {
        field: 'defaultExpenseCode',
        label: 'Expense Code',
        type: 'select',
        size: 6,
        options: expenseOptions.map((ec) => ({ value: ec.id, label: ec.name })),
        required: true,
      },
      { field: 'cost', label: 'Cost', type: 'number', size: 6, required: true },
      { field: 'notes', label: 'Notes', type: 'textarea', size: 12 },
    ],
    [serviceOptions, expenseOptions],
  );

  const { renderField: renderServiceModalField } = useFormBuilder(
    serviceModalFields,
    serviceForm,
    setServiceForm,
    serviceModalErrors,
  );

  const equipmentModalFields = useMemo(
    () => [
      { field: 'name', label: 'Name', type: 'text', size: 12, required: true },
      { field: 'quantity', label: 'Quantity', type: 'number', size: 6, required: true },
      { field: 'notes', label: 'Notes', type: 'textarea', size: 12 },
    ],
    [],
  );

  const { renderField: renderEquipmentModalField } = useFormBuilder(
    equipmentModalFields,
    equipmentForm,
    setEquipmentForm,
    equipmentModalErrors,
  );

  const computePartsTotal = (materials) =>
    materials.reduce(
      (sum, m) =>
        sum + (parseFloat(m.partsPrice || 0) || 0) * (parseFloat(m.quantity || 0) || 0),
      0,
    );
  const computeLabourTotal = (lab) =>
    lab.reduce(
      (sum, l) =>
        sum +
        (parseFloat(l.labourHours || 0) || 0) *
        (parseFloat(l.labourRate || 0) || 0) *
        (parseFloat(l.qty || 1) || 1),
      0,
    );
  const computeServicesTotal = (serv) =>
    serv.reduce(
      (sum, s) =>
        sum + (parseFloat(s.cost || 0) || 0) * (parseFloat(s.quantity || 1) || 1),
      0,
    );
  // keep track of parts price total
  useEffect(() => {
    if (!modalTask) return;
    const total = computePartsTotal(taskMaterials);
    if (!partsOverride) {
      setModalTask((prev) => ({ ...prev, partsPrice: total }));
    }
  }, [taskMaterials, partsOverride]);

  // keep track of labour cost total
  useEffect(() => {
    if (!modalTask) return;
    const total = computeLabourTotal(taskLabour);
    if (!labourOverride) {
      setModalTask((prev) => ({ ...prev, labourCost: total }));
    }
  }, [taskLabour, labourOverride]);

  // keep track of misc price total
  useEffect(() => {
    if (!modalTask) return;
    const total = computeServicesTotal(taskServices);
    if (!servicesOverride) {
      setModalTask((prev) => ({ ...prev, miscPrice: total }));
    }
  }, [taskServices, servicesOverride]);

  useEffect(() => {
    if (!modalTask) return;
    if (!servicesOverride) {
      const total = computeServicesTotal(taskServices);
      setModalTask((prev) => ({ ...prev, miscPrice: total }));
    }
  }, [servicesOverride]);

  useEffect(() => {
    if (!modalTask) return;
    if (!partsOverride) {
      const total = computePartsTotal(taskMaterials);
      setModalTask((prev) => ({ ...prev, partsPrice: total }));
    }
  }, [partsOverride]);

  useEffect(() => {
    if (!modalTask) return;
    if (!labourOverride) {
      const total = computeLabourTotal(taskLabour);
      setModalTask((prev) => ({ ...prev, labourCost: total }));
    }
  }, [labourOverride]);

  useEffect(() => {
    (async () => {
      try {
        const result = await getParts({ pageSize: 10 });
        const items = result.data || result;
        const opts = items.map((p) => ({
          value: p.id,
          label: `${p.partNumber} – ${p.description}`,
          raw: p,
        }));
        setInitialPartOptions(opts);
      } catch (err) {
        console.error('Error fetching initial parts:', err);
      }
    })();
  }, []);

  const assetMetersForAsset = useMemo(
    () => assetMeters.filter((am) => Number(am.assetId) === Number(assetId)),
    [assetMeters, assetId],
  );

  const hasAssetMeters = assetMetersForAsset.length > 0;

  const primaryAssetMeter = useMemo(
    () => assetMetersForAsset.find((am) => Number(am.primaryMeter) === 1) || null,
    [assetMetersForAsset],
  );

  const assetMeterOptions = useMemo(() => {
    return assetMetersForAsset.map((am) => ({
      value: am.id,
      label: am.meter?.name || '',
      unitCode: am.meter?.measurementUnits?.unitCode || '',
    }));
  }, [assetMetersForAsset]);

  const selectedAssetMeter = useMemo(() => {
    if (!modalTask) return null;
    if (modalTask.assetMeter && modalTask.assetMeter.meter) {
      return modalTask.assetMeter;
    }
    const assetMeterIdValue =
      typeof modalTask.assetMeterId === 'object'
        ? modalTask.assetMeterId?.id
        : modalTask.assetMeterId;
    if (assetMeterIdValue !== undefined && assetMeterIdValue !== null && assetMeterIdValue !== '') {
      const numericId = Number(assetMeterIdValue);
      if (Number.isFinite(numericId)) {
        const match = assetMetersForAsset.find((am) => Number(am.id) === numericId);
        if (match) {
          return match;
        }
      }
    }
    return null;
  }, [modalTask?.assetMeter, modalTask?.assetMeterId, assetMetersForAsset]);

  useEffect(() => {
    if (
      !showModal ||
      !isNewModalTask ||
      !modalTask ||
      defaultMeterAppliedRef.current ||
      assetMetersForAsset.length === 0
    ) {
      return;
    }

    if (modalTask.calendarBased !== 1) {
      defaultMeterAppliedRef.current = true;
      return;
    }

    setModalTask((prev) => {
      if (!prev) return prev;
      if (prev.calendarBased !== 1 || prev.planningTask !== 0) {
        return prev;
      }
      const next = {
        ...prev,
        calendarBased: 0,
        planningTask: 1,
      };
      if (!prev.assetMeterId && primaryAssetMeter) {
        next.assetMeterId = primaryAssetMeter.id;
      }
      return next;
    });
    defaultMeterAppliedRef.current = true;
  }, [
    assetMetersForAsset.length,
    isNewModalTask,
    modalTask,
    primaryAssetMeter,
    setModalTask,
    showModal,
  ]);

  useEffect(() => {
    setModalTask((prev) => {
      if (!prev || Number(prev.planningTask) === 1) {
        return prev;
      }

      let changed = false;
      const next = { ...prev };

      if (prev.schedulingType !== 'simple') {
        next.schedulingType = 'simple';
        changed = true;
      }

      if (prev.schedulingGroupId !== null && prev.schedulingGroupId !== undefined) {
        next.schedulingGroupId = null;
        changed = true;
      }

      if (Number(prev.schedulingPriority) !== 1) {
        next.schedulingPriority = 1;
        changed = true;
      }

      if (Number(prev.schedulingWindow) !== 48) {
        next.schedulingWindow = 48;
        changed = true;
      }

      return changed ? next : prev;
    });
  }, [
    modalTask?.planningTask,
    modalTask?.schedulingGroupId,
    modalTask?.schedulingPriority,
    modalTask?.schedulingType,
    modalTask?.schedulingWindow,
    setModalTask,
  ]);

  const lastDoneUnitName = selectedAssetMeter?.meter?.measurementUnits?.unitName;
  const lastDoneLabel = useMemo(
    () => (lastDoneUnitName ? `Last Known Change in ${lastDoneUnitName}` : 'Last Known Change'),
    [lastDoneUnitName],
  );

  useEffect(() => {
    if (!showModal) {
      return;
    }
    const meter = selectedAssetMeter;
    if (!meter) {
      setLatestMeterReading(null);
      return;
    }
    const rawMeterId = meter.meterId ?? meter.meter?.id;
    const rawTaskId = modalTask?.id;
    const rawAssetId = modalTask?.assetId ?? assetId;
    if (!rawMeterId || !rawTaskId || !rawAssetId) {
      setLatestMeterReading(null);
      return;
    }
    const meterId = Number(rawMeterId);
    const taskId = Number(rawTaskId);
    const derivedAssetId = Number(rawAssetId);
    if (!Number.isFinite(meterId) || !Number.isFinite(taskId) || !Number.isFinite(derivedAssetId)) {
      setLatestMeterReading(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const estimate = await estimateMeterReading({
          assetId: derivedAssetId,
          taskId,
          meterId,
          providedDate: new Date().toISOString(),
        });
        const estimatedValue =
          estimate && estimate.estimatedValue !== undefined && estimate.estimatedValue !== null
            ? Number(estimate.estimatedValue)
            : null;
        if (!cancelled) {
          setLatestMeterReading(Number.isFinite(estimatedValue) ? estimatedValue : null);
        }
      } catch (err) {
        console.error('Error fetching estimated meter reading', err);
        if (!cancelled) {
          setLatestMeterReading(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    assetId,
    modalTask?.assetId,
    modalTask?.id,
    selectedAssetMeter?.meterId,
    selectedAssetMeter?.meter?.id,
    showModal,
  ]);

  const taskTableFilters = useMemo(
    () => [
      { field: 'taskTypeId', type: 'dropDown', label: 'Task Type', api: 'task-types', displayField: 'taskTypeName', menuBarFilter: 1 },
      { field: 'code', type: 'dropDown', label: 'Task Code', api: 'task-codes', displayField: 'name', menuBarFilter: 1 },
      {
        field: 'defaultCostCentre',
        type: 'dropDown',
        label: 'Cost Centre',
        api: 'cost-centres',
        displayField: 'costCenterDescription',
        menuBarFilter: 0
      },
      { field: 'planningTask', type: 'switch', label: 'Planning Task', menuBarFilter: 1 },
      { field: 'statutory', type: 'switch', label: 'Statutory', menuBarFilter: 1 },
    ],
    [],
  );

  const {
    appliedFilters,
    appliedFilterParams,
    renderFilterControls
  } = useTableFilters(taskTableFilters);

  const baseSchedulingTypeOptions = useMemo(() => [
    { value: 'simple', label: 'Simple' },
    { value: 'suppression', label: 'Suppression' },
    { value: 'cycle', label: 'Cycle' },
  ], []);

  const schedulingTypeOptions = useMemo(
    () => (
      Boolean(modalTask?.planningTask)
        ? baseSchedulingTypeOptions
        : baseSchedulingTypeOptions.filter((opt) => opt.value === 'simple')
    ),
    [baseSchedulingTypeOptions, modalTask?.planningTask],
  );

  const schedulingWindowOptions = useMemo(() => [
    { value: 1, label: '1 Day' },
    { value: 2, label: '2 Days' },
    { value: 3, label: '3 Days' },
    { value: 5, label: '5 Days' },
    { value: 7, label: '1 Week' },
    { value: 30, label: '1 Month' },
    { value: 60, label: '2 Months' },
    { value: 90, label: '3 Months' },
    { value: 120, label: '4 Months' },
    { value: 180, label: '6 Months' },
    { value: 365, label: '1 Year' },
  ], []);

  const schedulingPriorityOptions = useMemo(() => [
    { value: 1, label: '1' },
    { value: 2, label: '2' },
    { value: 3, label: '3' },
    { value: 4, label: '4' },
    { value: 5, label: '5' },
  ], []);

  const forecastBasisOptions = useMemo(() => [
    { value: 'lastdone', label: 'Last Done' },
    // { value: 'lastscheduled', label: 'Last Scheduled' },
    { value: 'fixed', label: 'Fixed' },
  ], []);

  const validateFrequency = (t) => {
    if (t?.schedulingType === 'cycle') {
      return null;
    }
    const freq = Number(t.frequency) || 0;
    // Validate metr based frequency
    if (t?.schedulingType !== 'suppression' || !t?.planningTask) {
      if (freq < 1) return 'Frequency must be a number greater than 0';
    }
    // Validate calendar based frequency
    if (t.calendarBased) {
      if (freq < 1 || freq > 10000) return 'Frequency must be between 1 and 10,000';
    } else {
      const meter = assetMeters.find((m) => m.id === t.assetMeterId);
      const util = meter?.utilisation;
      if (!util) return 'Selected meter utilisation is required';
      if (freq === 0) return 'Frequency cannot be 0';
      if (util / freq >= 366)
        return 'Exceeds maximum number of occurences per year';
    }
    return null;
  };

  const cycleGroupFields = useMemo(
    () => [
      {
        field: 'name',
        label: 'Cycle Group Name',
        type: 'text',
        size: 12,
        required: modalTask?.schedulingType === 'cycle',
        tooltip: 'A Cycle Group will schedule tasks is order T1 - T2 - T3 and then repeat.'
      },
    ],
    [modalTask?.schedulingType],
  );

  const {
    errors: cycleFormErrors,
    setErrors: setCycleFormErrors,
    validate: validateCycleGroupField,
  } = useValidator(cycleGroupFields, cycleForm);

  const resetCycleState = useCallback(() => {
    setCycleForm({ name: '' });
    setCycleRows([]);
    setCycleRowErrors({});
    setCycleFormErrors({});
  }, [setCycleFormErrors]);

  const applySchedulingTypeChange = useCallback(
    (nextType) => {
      setModalTask((prev) => {
        if (!prev) return prev;
        const updated = { ...prev, schedulingType: nextType };
        if (nextType !== 'cycle') {
          updated.cycleGroupId = null;
        }
        return updated;
      });
      if (nextType !== 'cycle') {
        resetCycleState();
      }
    },
    [resetCycleState, setModalTask],
  );

  const handleSchedulingTypeConfirm = useCallback(() => {
    if (!pendingSchedulingType) {
      setShowSchedulingTypeConfirm(false);
      return;
    }
    applySchedulingTypeChange(pendingSchedulingType);
    setPendingSchedulingType(null);
    setShowSchedulingTypeConfirm(false);
  }, [applySchedulingTypeChange, pendingSchedulingType]);

  const handleSchedulingTypeCancel = useCallback(() => {
    setPendingSchedulingType(null);
    setShowSchedulingTypeConfirm(false);
  }, []);

  const handleSchedulingTypeSelect = useCallback(
    (nextType) => {
      if (!modalTask) return;
      if (modalTask.schedulingType === nextType) return;
      if (
        modalTask.schedulingType === 'cycle' &&
        nextType !== 'cycle' &&
        (modalTask.cycleGroupId || cycleRows.length > 0)
      ) {
        setPendingSchedulingType(nextType);
        setShowSchedulingTypeConfirm(true);
        return;
      }
      applySchedulingTypeChange(nextType);
    },
    [applySchedulingTypeChange, cycleRows.length, modalTask],
  );

  const baseTaskFormFields = useMemo(() => getTaskFormFields(), []);

  const taskFormFields = useMemo(
    () => {
      const fields = baseTaskFormFields.map((field) => {
        if (field.field === 'codeId') {
          return {
            ...field,
          };
        }

        return field;
      });

      if (Number(useCostResponsibility) === 1) {
        const nameIndex = fields.findIndex((field) => field.field === 'name');
        const insertIndex = nameIndex === -1 ? fields.length : nameIndex + 1;
        fields.splice(insertIndex, 0, {
          field: 'costResponsibilityId',
          label: 'Cost Responsibility',
          type: 'select',
          size: 4,
          required: true,
          api: 'cost-responsibility',
          displayField: 'name',
        });
      }

      return fields;
    },
    [baseTaskFormFields, useCostResponsibility],
  );

  const dynamicTaskFormFields = useMemo(
    () => getDynamicTaskFormFields(taskFormFields, showTaskRefSource),
    [taskFormFields, showTaskRefSource]
  );

  const forecastBasisField = useMemo(() => {
    return {
      field: 'forecastBasis',
      label: 'Schedule Basis',
      type: 'radio',
      size: 4,
      options: forecastBasisOptions,
    }
  }, [forecastBasisOptions, modalTask?.schedulingType]);

  const schedulingFormFields = useMemo(() => {
    const planning = Boolean(modalTask?.planningTask);
    const calendar = modalTask?.calendarBased !== undefined ? Boolean(modalTask.calendarBased) : true;
    const fields = [
      {
        field: 'schedulingType',
        label: 'Scheduling Type',
        type: 'radio',
        size: 4,
        options: schedulingTypeOptions,
        onChange: handleSchedulingTypeSelect,
      },
      {
        field: 'calendarBased',
        label: 'Calendar Based?',
        type: 'switch',
        size: 2,
        onChange: (nextValue, setFormData, field) => {
          setFormData((prev) => {
            if (!prev) return prev;
            const wasCalendarBased = Number(prev.calendarBased) === 1;
            const updated = { ...prev, [field]: nextValue };
            if (nextValue === 1 && !wasCalendarBased) {
              updated.assetMeterId = null;
            }
            return updated;
          });
        },
      },
      {
        field: 'planningTask',
        label: 'Planning Task?',
        type: 'switch',
        size: 2,
        disabled: modalTask?.schedulingType === 'suppression' || modalTask?.schedulingType === 'cycle',
      },
    ];

    // if (!isCycleScheduling) {
    fields.push({
      field: 'frequency',
      label:
        modalTask?.schedulingType === 'cycle'
          ? 'Interval'
          : calendar
            ? 'Frequency Days'
            : 'Frequency',
      type: 'number',
      size: 2,
      required: true,
      step: 1,
      inputMode: 'numeric',
    });
    // }

    if (!calendar) {
      fields.push({
        field: 'assetMeterId',
        label: 'Meter',
        type: 'select',
        size: 3,
        required: true,
        options: assetMeterOptions,
        formatOptionLabel: (opt) => `${opt.label} (${opt.unitCode})`,
      });
    }

    if (planning) {
      fields.push(
        { field: 'planningHorizon', label: 'Lead Time', type: 'select', size: 3, api: 'planning-horizon', displayField: 'name', optionSortField: 'sortOrder', required: true },
        //{ field: 'GeneralSpacer12', label: '', type: 'empty', size: 8 },
      );
    }

    fields.push(
      {
        field: 'schedulingGroupId',
        label: 'Scheduling Group',
        type: 'select',
        size: 4,
        options: schedulingGroupOptions.map((g) => ({ value: g.id, label: g.name, icon: g.icon })),
        formatOptionLabel: (opt) => (
          <span className="d-flex align-items-center gap-2">
            <Icon icon={opt.icon} className='text-xxl text-primary' style={{ position: 'relative', top: '-1px' }} /> {opt.label}
          </span>
        ),
        required: true,
      },
      { field: 'schedulingWindow', label: 'Scheduling Window', type: 'select', size: 4, options: schedulingWindowOptions, required: true },
      { field: 'schedulingPriority', label: 'Scheduling Priority', type: 'select', size: 4, options: schedulingPriorityOptions },
    );

    return fields.filter(
      (f) =>
        modalTask?.schedulingType === 'suppression' ||
        !['schedulingGroupId', 'schedulingWindow', 'schedulingPriority'].includes(f.field)
    );
  }, [
    modalTask?.planningTask,
    modalTask?.calendarBased,
    modalTask?.schedulingType,
    schedulingGroupOptions,
    assetMeterOptions,
  ]);

  useEffect(() => {
    if (!modalTask) return;
    if (modalTask.schedulingType === 'cycle' || modalTask.schedulingType === 'suppression') {
      if (modalTask.planningTask !== 1) {
        setModalTask((prev) => (prev ? { ...prev, planningTask: 1 } : prev));
      }
    }
  }, [modalTask, modalTask?.planningTask, modalTask?.schedulingType, setModalTask]);


  const templateFormFields = useMemo(
    () => [
      { field: 'name', label: 'Task Name', type: 'text', size: 6, required: true },
      { field: 'frequency', label: 'Frequency', type: 'number', size: 6, required: true },
      { field: 'planningTask', label: 'Planning Task', type: 'switch', size: 6 },
      { field: 'planningHorizon', label: 'Lead Time', type: 'select', size: 6, api: 'planning-horizon', displayField: 'name', optionSortField: 'sortOrder', required: true },
      { field: 'taskTypeId', label: 'Task Type', type: 'select', size: 6, api: 'task-types', displayField: 'taskTypeName', required: true },
      { field: 'defaultCostCentre', label: 'Cost Centre', type: 'select', size: 6, api: 'cost-centres', displayField: 'costCenterDescription', required: true },
      {
        field: 'codeId',
        label: 'Task Code',
        type: 'select',
        size: 6,
        options: taskCodeOptions,
        formatOptionLabel: renderCodeOptionLabel,
        required: true,
      },
      { field: 'partsExpense', label: 'Parts Expense Code', type: 'select', size: 6, api: 'expense-codes', displayField: 'name', required: true },
      { field: 'labourExpense', label: 'Labour Expense Code', type: 'select', size: 6, api: 'expense-codes', displayField: 'name', required: true },
      { field: 'miscExpense', label: 'Other Expense Code', type: 'select', size: 6, api: 'expense-codes', displayField: 'name', required: true },
      { field: 'partsPrice', label: 'Parts Cost', type: 'number', size: 6 },
      { field: 'labourHours', label: 'Labour Hours', type: 'number', size: 6 },
      { field: 'miscPrice', label: 'Other Price', type: 'number', size: 6 },
    ],
    [taskCodeOptions, renderCodeOptionLabel]
  );

  const updateCycleState = useCallback((name, rows) => {
    const normalizedName = typeof name === 'string' ? name : '';
    const normalizedRows =
      Array.isArray(rows) && rows.length
        ? rows.map((row) => createCycleRow(row))
        : [];
    setCycleForm({ name: normalizedName });
    setCycleRows(normalizedRows);
    setCycleRowErrors({});
  }, []);

  const buildDefaultCycleRows = useCallback(
    (task) => {
      const baseRow = createCycleRow({
        taskId:
          task?.id !== undefined && task?.id !== null
            ? task.id
            : CURRENT_TASK_PLACEHOLDER,
        meterId: resolveAssetMeterId(task),
      });
      const additionalRow = createCycleRow();
      return [baseRow, additionalRow];
    },
    [],
  );

  const loadCycleRecords = useCallback(
    async (task) => {
      if (!task) {
        updateCycleState('', []);
        return;
      }

      if (!task.id) {
        updateCycleState('', buildDefaultCycleRows(task));
        return;
      }

      if (!task.cycleGroupId) {
        updateCycleState('', buildDefaultCycleRows(task));
        return;
      }

      setIsCycleLoading(true);
      try {
        const group = await getTaskCycleGroupById(task.cycleGroupId);
        const groupName = typeof group?.name === 'string' ? group.name : '';
        const records = Array.isArray(group?.tasks) ? group.tasks : [];

        if (!records.length) {
          updateCycleState(groupName, buildDefaultCycleRows(task));
          return;
        }

        const sortedRows = records
          .slice()
          .sort(
            (a, b) =>
              (Number(a.order) || 0) - (Number(b.order) || 0) ||
              (Number(a.id) || 0) - (Number(b.id) || 0),
          )
          .map((row) =>
            createCycleRow({
              id: row.id,
              clientId: `cycle-${row.id}`,
              taskId: row.taskId ?? null,
              interval: row.interval ?? row.startValue ?? '',
              meterId: row.meterId ?? null,
            }),
          );

        updateCycleState(groupName, sortedRows);
      } catch (err) {
        console.error('Error loading cycle group:', err);
        updateCycleState('', buildDefaultCycleRows(task));
      } finally {
        setIsCycleLoading(false);
      }
    },
    [buildDefaultCycleRows, updateCycleState],
  );

  useEffect(() => {
    if (!showModal) {
      resetCycleState();
      setShowSchedulingTypeConfirm(false);
      setPendingSchedulingType(null);
    }
  }, [resetCycleState, showModal]);

  useEffect(() => {
    if (!showModal || modalTask?.schedulingType !== 'cycle') {
      return;
    }
    loadCycleRecords(modalTask);
  }, [
    loadCycleRecords,
    //modalTask,
    modalTask?.schedulingType,
    showModal,
  ]);

  const normalizedFrequencyInterval = useMemo(() => {
    const raw = modalTask?.frequency;
    if (raw === undefined || raw === null) return '';
    return String(raw).replace(/[^0-9]/g, '');
  }, [modalTask?.frequency]);

  const handleAddCycleRow = useCallback(() => {
    setCycleRows((prev) => {
      if (!prev.length) {
        return buildDefaultCycleRows(modalTask);
      }
      return [...prev, createCycleRow({ interval: normalizedFrequencyInterval })];
    });
    setCycleRowErrors((prev) => {
      if (!prev.general) return prev;
      const next = { ...prev };
      delete next.general;
      return next;
    });
  }, [buildDefaultCycleRows, modalTask, normalizedFrequencyInterval]);

  const handleDeleteCycleRow = useCallback((clientId) => {
    setCycleRows((prev) => {
      if (prev.length <= 1) {
        return prev;
      }
      const next = prev.filter((row) => row.clientId !== clientId);
      return next.length ? next : prev;
    });
    setCycleRowErrors((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (key.startsWith(`${clientId}-`)) {
          delete next[key];
        }
      });
      if (next.general) {
        delete next.general;
      }
      return next;
    });
  }, []);

  const handleCycleRowChange = useCallback((clientId, field, value) => {
    setCycleRows((prev) =>
      prev.map((row) => {
        if (row.clientId !== clientId) return row;
        if (field === 'interval') {
          const sanitized = typeof value === 'string' ? value.replace(/[^0-9]/g, '') : '';
          return { ...row, interval: sanitized };
        }
        if (field === 'taskId') {
          return { ...row, taskId: value || null, meterId: null };
        }
        return row;
      }),
    );
    const errorKey = `${clientId}-${field}`;
    setCycleRowErrors((prev) => {
      const next = { ...prev };
      if (next[errorKey]) {
        delete next[errorKey];
      }
      if (next.general) {
        delete next.general;
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!showModal) return;
    if (modalTask?.schedulingType !== 'cycle') return;

    setCycleRows((prev) => {
      if (!prev.length) return prev;
      const next = prev.map((row) => {
        if (row.interval === normalizedFrequencyInterval) return row;
        return { ...row, interval: normalizedFrequencyInterval };
      });
      return next;
    });
  }, [modalTask?.schedulingType, normalizedFrequencyInterval, showModal]);

  const handleCycleDragEnd = useCallback((result) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;
    setCycleRows((prev) => {
      if (!prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(result.source.index, 1);
      next.splice(result.destination.index, 0, moved);
      return next;
    });
  }, []);

  const baseMeterId = useMemo(() => {
    const meterFromTask = resolveAssetMeterId(modalTask);
    if (meterFromTask !== null) return meterFromTask;
    const selectedMeterId = Number(selectedAssetMeter?.id);
    return Number.isFinite(selectedMeterId) ? selectedMeterId : null;
  }, [modalTask, selectedAssetMeter?.id]);

  const taskMeterMap = useMemo(() => {
    const map = new Map();
    tasks.forEach((task) => {
      const taskId = Number(task.id);
      if (!Number.isFinite(taskId)) return;
      const meterId = resolveAssetMeterId(task);
      if (meterId !== null) {
        map.set(taskId, meterId);
      }
    });
    return map;
  }, [tasks]);

  const formatTaskLabel = useCallback(
    (task) => {
      if (!task) return '';
      const codeValue =
        task?.code?.name ?? '';
      return (
        formatCodeWithName(codeValue, task?.name) ||
        task?.name ||
        (task?.id ? `Task ${task.id}` : '')
      );
    },
    [formatCodeWithName],
  );

  const baseTaskLabel = useMemo(() => formatTaskLabel(modalTask) || 'This Task', [
    formatTaskLabel,
    modalTask,
  ]);

  const cycleTaskOptions = useMemo(() => {
    const options = tasks
      .filter((task) => {
        // If this is a calendar task, include it
        if (task.calendarBased === 1 && task.planningTask === 1) {
          return true;
        }

        // Otherwise fall back to meter-matching logic
        const meterId = resolveAssetMeterId(task);
        if (meterId === null) return false;
        if (task?.schedulingType === "suppression") return false;
        return Number(meterId) === Number(baseMeterId);
      })
      .map((task) => ({
        value: String(task.id),
        label: formatTaskLabel(task),
      }));

    if (modalTask) {
      const currentValue =
        modalTask.id !== undefined && modalTask.id !== null
          ? String(modalTask.id)
          : CURRENT_TASK_PLACEHOLDER;
      if (!options.some((opt) => opt.value === currentValue)) {
        options.unshift({ value: currentValue, label: baseTaskLabel });
      }
    }

    return options;
  }, [baseMeterId, baseTaskLabel, formatTaskLabel, modalTask, tasks]);

  const validateCycleConfiguration = useCallback(() => {
    if (modalTask?.schedulingType !== 'cycle') {
      setCycleRowErrors({});
      setCycleFormErrors({});
      return true;
    }

    const isGroupValid = validateCycleGroupField();
    const nextErrors = {};
    const generalMessages = [];

    if (cycleRows.length < 2) {
      generalMessages.push('Add at least two tasks to create a cycle.');
    }

    if (!baseMeterId && !modalTask?.calendarBased) {
      generalMessages.push('Select a meter to configure the cycle.');
    }

    const requiredTaskValue =
      modalTask?.id !== undefined && modalTask?.id !== null
        ? String(modalTask.id)
        : CURRENT_TASK_PLACEHOLDER;
    const includesCurrentTask = cycleRows.some((row) => {
      if (!row.taskId) return false;
      if (requiredTaskValue === CURRENT_TASK_PLACEHOLDER) {
        return row.taskId === CURRENT_TASK_PLACEHOLDER;
      }
      if (row.taskId === CURRENT_TASK_PLACEHOLDER) {
        return false;
      }
      return Number(row.taskId) === Number(requiredTaskValue);
    });

    if (!includesCurrentTask) {
      generalMessages.push('Include the task you are editing in the cycle');
    }

    cycleRows.forEach((row) => {
      if (!row.taskId) {
        nextErrors[`${row.clientId}-taskId`] = 'Select a task';
      }
      const intervalValue = Number(row.interval);
      if (!Number.isInteger(intervalValue) || intervalValue <= 0) {
        nextErrors[`${row.clientId}-interval`] = 'Enter a number';
      }
    });

    if (generalMessages.length) {
      nextErrors.general = generalMessages.join(' ');
    }

    setCycleRowErrors(nextErrors);
    return isGroupValid && Object.keys(nextErrors).length === 0;
  }, [
    modalTask?.calendarBased,
    baseMeterId,
    cycleRows,
    modalTask?.id,
    modalTask?.schedulingType,
    setCycleFormErrors,
    validateCycleGroupField,
  ]);

  const buildCycleConfigurationPayload = useCallback(() => {
    if (modalTask?.schedulingType !== 'cycle') {
      return null;
    }

    const trimmedName = (cycleForm.name || '').trim();
    if (!trimmedName || !cycleRows.length) {
      return null;
    }

    return {
      cycleGroupId: modalTask?.cycleGroupId ?? null,
      name: trimmedName,
      rows: cycleRows.map((row, index) => {
        const rawTaskId = row.taskId;
        const resolvedTaskId =
          rawTaskId === CURRENT_TASK_PLACEHOLDER ? CURRENT_TASK_PLACEHOLDER : rawTaskId;
        const numericTaskId =
          resolvedTaskId && resolvedTaskId !== CURRENT_TASK_PLACEHOLDER
            ? Number(resolvedTaskId)
            : modalTask?.id ?? null;
        let resolvedMeterId = row.meterId ?? null;

        if (!resolvedMeterId) {
          if (resolvedTaskId === CURRENT_TASK_PLACEHOLDER) {
            resolvedMeterId = baseMeterId ?? null;
          } else if (numericTaskId && taskMeterMap.has(Number(numericTaskId))) {
            resolvedMeterId = taskMeterMap.get(Number(numericTaskId)) ?? null;
          } else if (baseMeterId) {
            resolvedMeterId = baseMeterId;
          }
        }

        return {
          id: row.id ?? null,
          taskId: resolvedTaskId ?? null,
          interval: Number(row.interval) || null,
          order: index + 1,
          meterId: resolvedMeterId ?? null,
        };
      }),
    };
  }, [
    modalTask?.calendarBased,
    baseMeterId,
    cycleForm.name,
    cycleRows,
    modalTask?.cycleGroupId,
    modalTask?.id,
    modalTask?.schedulingType,
    taskMeterMap,
  ]);

  const taskDocumentFields = useMemo(
    () => [
      {
        field: 'taskFiles',
        label: 'Documents',
        type: 'files',
        size: 12,
        directory: 'taskfiles',
        accept:
          '.jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.bmp,.gif,.heic,.dwg,.dxf,.svg',
      },
    ],
    [],
  );

  const partsTabFields = useMemo(
    () => [
      {
        field: 'partsPrice',
        label: 'Total Parts Cost',
        type: 'number',
        size: 4,
        disabled: !partsOverride,
      },
      {
        field: 'partsOverride',
        label: 'Override',
        type: 'switch',
        size: 2,
        onChange: (checked) => setPartsOverride(Boolean(checked)),
      },
      {
        field: 'partsExpense',
        label: 'Parts Expense Code',
        type: 'select',
        size: 6,
        options: expenseOptions.map((ec) => ({ value: ec.id, label: ec.name })),
        required: true,
      },
    ],
    [expenseOptions, partsOverride],
  );

  const labourTabFields = useMemo(
    () => [
      {
        field: 'labourCost',
        label: 'Total Labour Cost',
        type: 'number',
        size: 4,
        disabled: !labourOverride,
      },
      {
        field: 'labourOverride',
        label: 'Override',
        type: 'switch',
        size: 2,
        onChange: (checked) => setLabourOverride(Boolean(checked)),
      },
      {
        field: 'labourExpense',
        label: 'Labour Expense Code',
        type: 'select',
        size: 6,
        options: expenseOptions.map((ec) => ({ value: ec.id, label: ec.name })),
        required: true,
      },
    ],
    [expenseOptions, labourOverride],
  );

  const serviceTabFields = useMemo(
    () => [
      {
        field: 'miscPrice',
        label: 'Total Other Cost',
        type: 'number',
        size: 4,
        disabled: !servicesOverride,
      },
      {
        field: 'servicesOverride',
        label: 'Override',
        type: 'switch',
        size: 2,
        onChange: (checked) => setServicesOverride(Boolean(checked)),
      },
      {
        field: 'miscExpense',
        label: 'Other Expense Code',
        type: 'select',
        size: 6,
        options: expenseOptions.map((ec) => ({ value: ec.id, label: ec.name })),
        required: true,
      },
    ],
    [expenseOptions, servicesOverride],
  );

  const handleRotableChange = useCallback((selected, setFormData = setModalTask) => {
    setFormData((prev) => ({
      ...prev,
      rotablesId: selected ? selected.value : null,
    }));
  }, []);

  const rotableTabFields = useMemo(
    () => [
      {
        field: 'rotablesId',
        label: 'Rotable',
        type: 'select',
        size: 12,
        options: rotableOptions,
        selectedOption: selectedRotableOption,
        isClearable: true,
        isLoading: rotablesLoading,
        placeholder: rotablesLoading ? 'Loading rotables...' : 'Select a rotable',
        onChange: (option, setFormData) => {
          handleRotableChange(option, setFormData);
        },
      },
      {
        field: 'rotableTransactionNotes',
        label: 'Transaction Notes',
        type: 'textarea',
        size: 12,
        lines: 4,
      },
      {
        field: 'rotableTransactionFiles',
        label: 'Files',
        type: 'files',
        size: 12,
        directory: 'partsfiles',
        accept:
          '.jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.bmp,.gif,.heic,.dwg,.dxf,.svg',
      },
      {
        field: 'rotableTransactionImages',
        label: 'Images',
        type: 'images',
        size: 12,
        directory: 'partsfiles',
      },
    ],
    [handleRotableChange, rotableOptions, rotablesLoading, selectedRotableOption],
  );

  const modalTaskValidationData = useMemo(
    () => ({ ...modalTask, partsOverride, labourOverride, servicesOverride }),
    [labourOverride, modalTask, partsOverride, servicesOverride],
  );

  const taskValidationFields = useMemo(
    () => [
      ...dynamicTaskFormFields,
      ...schedulingFormFields,
      ...partsTabFields,
      ...labourTabFields,
      ...serviceTabFields,
    ],
    [dynamicTaskFormFields, labourTabFields, partsTabFields, schedulingFormFields, serviceTabFields],
  );

  const {
    errors: modalErrors,
    setErrors: setModalErrors,
    validate: validateModalTaskForm,
  } = useValidator(taskValidationFields, modalTaskValidationData);

  const { renderField: renderTemplateField } = useFormBuilder(
    templateFormFields,
    templateTask,
    setTemplateTask,
    templateErrors,
  );

  const { renderField: renderTaskField } = useFormBuilder(
    taskFormFields,
    modalTask,
    setModalTask,
    modalErrors,
    { refreshKey: lookupRefreshKey },
  );
  const { renderField: renderSchedulingField } = useFormBuilder(
    schedulingFormFields,
    modalTask,
    setModalTask,
    modalErrors,
    { refreshKey: lookupRefreshKey },
  );

  const { renderField: renderCycleGroupField } = useFormBuilder(
    cycleGroupFields,
    cycleForm,
    setCycleForm,
    cycleFormErrors,
  );

  const { renderField: renderDocumentFields } = useFormBuilder(
    taskDocumentFields,
    modalTask,
    setModalTask,
    modalErrors,
  );

  const {
    renderField: renderPartsTabField,
  } = useFormBuilder(partsTabFields, { ...modalTask, partsOverride }, setModalTask, modalErrors);
  const { renderField: renderLabourTabField } = useFormBuilder(
    labourTabFields,
    { ...modalTask, labourOverride },
    setModalTask,
    modalErrors,
  );
  const { renderField: renderServiceTabField } = useFormBuilder(
    serviceTabFields,
    { ...modalTask, servicesOverride },
    setModalTask,
    modalErrors,
  );
  const { renderField: renderRotableTabField } = useFormBuilder(
    rotableTabFields,
    modalTask,
    setModalTask,
    modalErrors,
  );

  const tabHasErrors = (fields) => fields.some((f) => modalErrors[f.field]);
  const cycleTabHasErrors =
    modalTask?.schedulingType === 'cycle' &&
    (Object.keys(cycleFormErrors).length > 0 || Object.keys(cycleRowErrors).length > 0);
  const generalTabHasError = tabHasErrors([
    ...dynamicTaskFormFields.filter((field) => field.field !== 'lastDone'),
    { field: 'partsPrice' },
    { field: 'labourCost' },
    { field: 'miscPrice' },
  ]);
  const schedulingTabHasError =
    tabHasErrors([...schedulingFormFields, { field: 'lastDone' }]) || cycleTabHasErrors;
  const partsTabHasError = tabHasErrors(partsTabFields);
  const labourTabHasError = tabHasErrors(labourTabFields);
  const serviceTabHasError = tabHasErrors(serviceTabFields);

  const resetCopyState = useCallback(() => {
    setCopyForm({ sourceAssetId: null, deleteExisting: 0, costCentreId: null });
    setCopyErrors({});
    setCopySelectedAssetOption(null);
    setCopyPreviewTasks([]);
    setCopyLoadingTasks(false);
    setCopyProgress({ status: 'idle', message: '', current: 0, total: 0 });
    setCopyResult(null);
    setCopySubmitting(false);
    setCopyCompleted(false);
    setCopyTaskSelectionError('');
  }, []);

  const COPY_ASSET_PAGE_SIZE = 25;

  const handleCopyAssetSearch = useCallback(
    async (inputValue, { page } = {}) => {
      const raw = typeof inputValue === 'string' ? inputValue : inputValue?.search ?? '';
      const trimmed = raw.trim();
      const pageNumber = typeof page === 'number' && page > 0 ? page : 1;
      const shouldLoadDefault = trimmed === '';

      if (!shouldLoadDefault && trimmed.length < 2) {
        return { options: [], hasMore: false };
      }

      try {
        const resp = await getPaginatedAssets({
          page: pageNumber,
          pageSize: COPY_ASSET_PAGE_SIZE,
          search: shouldLoadDefault ? '' : trimmed,
        });
        const items = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
        const filteredItems = items.filter((item) => item.id !== assetId);
        const options = filteredItems.map((item) => ({
          value: item.id,
          label: `${item.assetNumber} – ${item.assetDescription}`,
        }));
        const totalCount =
          typeof resp?.total === 'number'
            ? resp.total
            : typeof resp?.count === 'number'
              ? resp.count
              : undefined;
        const hasMore =
          typeof resp?.hasMore === 'boolean'
            ? resp.hasMore
            : typeof totalCount === 'number'
              ? pageNumber * COPY_ASSET_PAGE_SIZE < totalCount
              : items.length === COPY_ASSET_PAGE_SIZE;

        return {
          options,
          totalCount,
          pageSize: COPY_ASSET_PAGE_SIZE,
          hasMore,
        };
      } catch (err) {
        console.error('Error fetching assets for copy:', err);
        return { options: [], hasMore: false };
      }
    },
    [assetId],
  );

  const handleCopyAssetChange = useCallback(
    async (option, setFormData, field, setSelectedOptionFn) => {
      if (typeof setSelectedOptionFn === 'function') {
        setSelectedOptionFn(option);
      }
      setFormData((prev) => ({ ...prev, [field]: option?.value || null }));
      setCopyResult(null);
      setCopyCompleted(false);
      setCopyProgress({ status: 'idle', message: '', current: 0, total: 0 });
      setCopyTaskSelectionError('');
      setCopyErrors((prev) => {
        if (!prev.sourceAssetId) return prev;
        const next = { ...prev };
        delete next.sourceAssetId;
        return next;
      });

      if (!option?.value) {
        setCopyPreviewTasks([]);
        setCopyTaskSelectionError('');
        return;
      }

      const selectedId = Number(option.value);
      if (!Number.isFinite(selectedId)) {
        setCopyPreviewTasks([]);
        setCopyTaskSelectionError('');
        return;
      }

      if (selectedId === assetId) {
        setCopyErrors((prev) => ({
          ...prev,
          sourceAssetId: 'Select a different asset to copy from.',
        }));
        setCopyPreviewTasks([]);
        setCopyTaskSelectionError('');
        return;
      }

      setCopyLoadingTasks(true);
      try {
        const fetched = await getTasksByAsset(selectedId);
        const items = Array.isArray(fetched) ? fetched : [];
        const mapped = items.map((task) => ({
          id: task.id,
          name: task.name,
          code: task.code?.name,
          type: task.taskType?.typeDescription || task.taskType?.name || '',
          frequency: task.frequency,
          selected: true,
        }));
        setCopyPreviewTasks(mapped);
        setCopyProgress({
          status: 'idle',
          message: '',
          current: 0,
          total: mapped.filter((task) => task.selected !== false).length,
        });
      } catch (err) {
        console.error('Error loading source tasks for copy:', err);
        showToastError('Unable to load tasks for the selected asset.');
        setCopyPreviewTasks([]);
        setCopyProgress({ status: 'idle', message: '', current: 0, total: 0 });
        setCopyTaskSelectionError('');
      } finally {
        setCopyLoadingTasks(false);
      }
    },
    [assetId, showToastError],
  );

  const copyFieldConfigs = useMemo(
    () => [
      {
        field: 'sourceAssetId',
        label: 'Select Asset',
        type: 'paginated-select',
        size: 12,
        loadOptions: handleCopyAssetSearch,
        selectedOption: copySelectedAssetOption,
        setSelectedOption: setCopySelectedAssetOption,
        onChange: handleCopyAssetChange,
        required: true,
      },
      {
        field: 'costCentreId',
        label: 'Cost Centre (optional)',
        type: 'select',
        size: 12,
        api: 'cost-centres',
        displayField: 'costCenterDescription',
        isClearable: true,
      },
      {
        field: 'deleteExisting',
        label: 'Delete existing tasks',
        type: 'switch',
        size: 3,
      }, ,
    ],
    [copySelectedAssetOption, handleCopyAssetSearch, handleCopyAssetChange],
  );

  const { renderField: renderCopyField } = useFormBuilder(
    copyFieldConfigs,
    copyForm,
    setCopyForm,
    copyErrors,
  );

  const copyStats = useMemo(
    () => ({
      importedTasks: Number(copyResult?.importedTaskCount) || 0,
      deletedTasks: Number(copyResult?.deletedTaskCount) || 0,
      deletedWorkOrders: Number(copyResult?.deletedWorkOrderCount) || 0,
      skippedWorkOrders: Number(copyResult?.skippedWorkOrderCount) || 0,
      skippedTasks: Number(copyResult?.skippedTaskCount) || 0,
      createdAssetMeters: Number(copyResult?.createdAssetMeterCount) || 0,
      sourceTasks: Number(copyResult?.sourceTaskCount) || 0,
    }),
    [copyResult],
  );

  const copySelectedTaskCount = useMemo(
    () => copyPreviewTasks.filter((task) => task.selected !== false).length,
    [copyPreviewTasks],
  );

  const calculateTaskCost = useCallback((task) => {
    try {
      const labour = parseFloat(task.labourCost || 0) || 0;
      const parts = parseFloat(task.partsPrice || 0) || 0;
      const misc = parseFloat(task.miscPrice || 0) || 0;
      const total = labour + parts + misc;
      setTaskCosts((prev) => ({ ...prev, [task.id]: total }));
    } catch (err) {
      console.error('Error calculating task cost', err);
      setTaskCosts((prev) => ({ ...prev, [task.id]: 0 }));
    }
  }, []);

  const handleCopyTaskToggle = useCallback(
    (taskId) => {
      setCopyPreviewTasks((prev) =>
        prev.map((task) => {
          if (task.id !== taskId) {
            return task;
          }
          const isSelected = task.selected !== false;
          return { ...task, selected: !isSelected };
        }),
      );
      setCopyTaskSelectionError('');
    },
    [setCopyPreviewTasks, setCopyTaskSelectionError],
  );

  const openCopyModal = useCallback(() => {
    resetCopyState();
    setShowCopyFromAssetModal(true);
  }, [resetCopyState]);

  const handleCopyModalClose = useCallback(() => {
    setShowCopyFromAssetModal(false);
    resetCopyState();
  }, [resetCopyState]);

  const loadTasksByAsset = useCallback(async (id) => {
    if (!id) return;
    setIsTasksLoading(true);
    try {
      const params = {};

      if (Array.isArray(appliedFilterParams?.taskTypeId) && appliedFilterParams.taskTypeId.length) {
        params.taskTypeId = appliedFilterParams.taskTypeId;
      }

      if (Array.isArray(appliedFilterParams?.defaultCostCentre) && appliedFilterParams.defaultCostCentre.length) {
        params.defaultCostCentre = appliedFilterParams.defaultCostCentre;
      }

      if (Array.isArray(appliedFilterParams?.code) && appliedFilterParams.code.length) {
        params.code = appliedFilterParams.code;
      }

      if (appliedFilterParams?.planningTask) {
        params.planningTask = 1;
      }

      if (appliedFilterParams?.statutory) {
        params.statutory = 1;
      }

      if (sortField && sortOrder && !['occ', 'cost', 'lcc'].includes(sortField)) {
        params.sortField = sortField;
        params.sortOrder = sortOrder;
      }

      const fetched = await getTasksByAsset(id, params);
      const items = Array.isArray(fetched) ? fetched : [];
      const totalLcc = fetched.reduce((sum, item) => sum + Number(item.totalTaskLccCost || 0), 0);
      const endOfLife = fetched[0]?.assetMeter?.endOfLife || null;
      const measurementUnits =
        (fetched[0]?.assetMeter?.meter?.measurementUnits?.unitName || '')
          .replace(/s$/, '') || null;
      const totalLccPerUnit = endOfLife && endOfLife > 0 ? totalLcc / endOfLife || 0 : 0;

      setMeasurementUnits(measurementUnits);
      setOccCounts({});
      setTaskCosts({});
      setTasks(items);
      setTotalLcc(totalLcc);
      setTotalLccPerUnit(totalLccPerUnit);

      items.forEach((t) => {
        calculateTaskCost(t);
      });
    } catch (err) {
      console.error('Error loading tasks:', err);
    } finally {
      setIsTasksLoading(false);
    }
  }, [appliedFilterParams, calculateTaskCost, sortField, sortOrder]);

  const handleCopyImport = useCallback(async () => {
    const selectedAssetId = copyForm.sourceAssetId ? Number(copyForm.sourceAssetId) : null;
    const errors = {};

    if (!selectedAssetId) {
      errors.sourceAssetId = 'Please select an asset to copy from.';
    } else if (selectedAssetId === assetId) {
      errors.sourceAssetId = 'Select a different asset to copy from.';
    }

    const selectedTaskIds = copyPreviewTasks
      .filter((task) => task.selected !== false)
      .map((task) => task.id)
      .filter((id) => id !== undefined && id !== null);

    if (!selectedTaskIds.length) {
      setCopyTaskSelectionError('Select at least one task to copy.');
    }

    if (Object.keys(errors).length) {
      setCopyErrors(errors);
      return;
    }

    if (!selectedTaskIds.length) {
      return;
    }

    setCopyErrors({});
    setCopyCompleted(false);
    setCopySubmitting(true);
    setCopyResult(null);
    setCopyTaskSelectionError('');
    const totalTasks = selectedTaskIds.length;
    setCopyProgress({
      status: 'working',
      message: copyForm.deleteExisting ? 'Deleting existing tasks and preparing import…' : 'Preparing task import…',
      current: 0,
      total: totalTasks,
    });

    try {
      const payload = {
        sourceAssetId: selectedAssetId,
        targetAssetId: assetId,
        deleteExisting: Boolean(copyForm.deleteExisting),
        taskIds: selectedTaskIds,
      };

      if (copyForm.costCentreId) {
        payload.costCentreId = Number(copyForm.costCentreId);
      }

      const response = await copyTasksFromAsset(payload);
      const importedCount = Number(response?.importedTaskCount) || 0;
      const skippedTasks = Number(response?.skippedTaskCount) || 0;
      const sourceCount = Number(response?.sourceTaskCount) || totalTasks;
      const successMessage = skippedTasks > 0
        ? `Imported ${importedCount} task${importedCount === 1 ? '' : 's'}. ${skippedTasks} existing task${skippedTasks === 1 ? '' : 's'} were kept because they have modified work orders.`
        : `Imported ${importedCount} task${importedCount === 1 ? '' : 's'}.`;

      setCopyProgress({
        status: 'success',
        message: successMessage,
        current: importedCount,
        total: sourceCount,
      });
      setCopyResult(response);
      setCopyCompleted(true);
      showToastSuccess('Tasks imported successfully.');
      await loadTasksByAsset(assetId);
    } catch (err) {
      console.error('Error copying tasks from asset:', err);
      const message = typeof err?.response?.data?.message === 'string'
        ? err.response.data.message
        : 'Failed to import tasks from the selected asset.';
      setCopyProgress({ status: 'error', message, current: 0, total: totalTasks });
      showToastError(message);
    } finally {
      setCopySubmitting(false);
    }
  }, [copyForm, copyPreviewTasks, assetId, loadTasksByAsset, copyTasksFromAsset, showToastError, showToastSuccess, setCopyTaskSelectionError]);

  useEffect(() => {
    loadTasksByAsset(assetId);
  }, [assetId, appliedFilters, loadTasksByAsset]);

  const handleSortChange = useCallback(
    (field) => {
      if (sortField === field) {
        if (sortOrder === 'asc') {
          setSortOrder('desc');
        } else if (sortOrder === 'desc') {
          setSortField(null);
          setSortOrder(null);
        } else {
          setSortOrder('asc');
        }
      } else {
        setSortField(field);
        setSortOrder('asc');
      }
    },
    [sortField, sortOrder],
  );

  const getSortIcon = (field) => {
    if (sortField !== field || !sortOrder) {
      return 'mdi:sort';
    }
    return sortOrder === 'asc' ? 'mdi:sort-ascending' : 'mdi:sort-descending';
  };

  const renderSortableHeader = (label, field) => {
    const ariaSort =
      sortField === field
        ? sortOrder === 'asc'
          ? 'ascending'
          : sortOrder === 'desc'
            ? 'descending'
            : 'none'
        : 'none';

      return (
        <th
          onClick={() => handleSortChange(field)}
          style={{ ...stickyHeaderCellStyle, cursor: 'pointer' }}
          scope="col"
          aria-sort={ariaSort}
        >
          <span className="d-inline-flex align-items-center gap-1">
            {label}
          <Icon className="text-xl" icon={getSortIcon(field)} />
        </span>
      </th>
    );
  };

  const handleImportComplete = () => {
    setShowTaskImporter(false);
    loadTasksByAsset(assetId);
  };

  const loadTemplates = async () => {
    try {
      const resp = await getPaginatedMaintenancePlans({ pageSize: 1000 });
      const plans = resp.data || resp;
      const filtered = plans.filter(
        (p) =>
          !Array.isArray(p.groupIds) ||
          p.groupIds.length === 0 ||
          p.groupIds.includes(asset.groupId)
      );
      setTemplateOptions(
        filtered.map((p) => ({
          value: p.id,
          label: `${p.codeName ?? p.code ?? ''} ${p.name} ${p.frequency} ${p.taskType?.taskTypeName || ''}`.trim(),
          raw: p,
        }))
      );
    } catch (err) {
      console.error('Error loading templates:', err);
    }
  };

  const handleTemplateSelect = (sel) => {
    setSelectedTemplate(sel);
    if (!sel) return;
    const plan = sel.raw;
    const codeId = plan.codeId;
    setTemplateTask({
      name: plan.name,
      frequency: plan.frequency,
      planningTask: plan.planningTask,
      planningHorizon: plan.planningHorizonId,
      taskTypeId: plan.taskTypeId,
      defaultCostCentre: asset?.costCenter?.id || asset?.costCenterId || null,
      codeId,
      statutory: plan.statutory,
      partsExpense: plan.defaultPartsExpenseId,
      labourExpense: plan.defaultLabourExpenseId,
      miscExpense: plan.defaultMiscExpenseId,
      partsPrice: plan.partsCost,
      labourHours: plan.labourHours,
      miscPrice: plan.miscCost,
    });
  };

  const handleTemplateSave = async () => {
    const errors = validateForm(templateFormFields, templateTask);
    setTemplateErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      const payload = {
        ...templateTask,
        assetId,
        maintenancePlanId: selectedTemplate.value,
        codeId: templateTask.codeId,
      };
      delete payload.codeLabel;
      delete payload.modifierLabel;
      const created = await createTasks(payload);
      const newTaskId = created.id;
      const planId = selectedTemplate.value;

      const [mats, equip, labour, services] = await Promise.all([
        getBillOfMaterialsMpsByMaintenancePlanId(planId),
        getBillOfEquipmentMpsByMaintenancePlanId(planId),
        getBillOfLabourMpsByMaintenancePlanId(planId),
        getBillOfServicesMpsByMaintenancePlanId(planId),
      ]);

      await Promise.all(
        mats.map((m) =>
          createBillOfMaterials({
            taskId: newTaskId,
            partsId: m.partsId,
            optional: m?.optional || 0,
            quantity: m.quantity,
            defaultExpenseCode: m.defaultExpenseCode,
            partsPrice: m.partsPrice,
            pricingDate: m.pricingDate,
          })
        )
      );
      await Promise.all(
        equip.map((e) =>
          createBillOfEquipment({
            taskId: newTaskId,
            name: e.name,
            quantity: e.quantity,
            notes: e.notes,
          })
        )
      );
      await Promise.all(
        labour.map((l) =>
          createBillOfLabour({
            taskId: newTaskId,
            tradeId: l.tradeId,
            qty: l.qty,
            labourHours: l.labourHours,
            defaultExpenseCode: l.defaultExpenseCode,
            labourRate: l.labourRate,
          })
        )
      );
      await Promise.all(
        services.map((s) =>
          createBillOfServices({
            taskId: newTaskId,
            serviceId: s.serviceId,
            defaultExpenseCode: s.defaultExpenseCode,
            quantity: s.quantity,
          })
        )
      );

      setShowTemplateModal(false);
      setSelectedTemplate(null);
      setTemplateTask({});
      await loadTasksByAsset(assetId);
    } catch (err) {
      console.error('Error saving task from template:', err);
    }
  };

  const handleLastDoneToggle = () => {
    if (isLastDoneLocked) {
      setIsLastDoneLocked(false);
      setShowLastDoneWarning(true);
    } else {
      setIsLastDoneLocked(true);
      setShowLastDoneWarning(false);
    }
  };

  const handleLastDoneChange = (event) => {
    const { value } = event.target;
    if (!isLastDoneLocked) {
      lastDoneEditedRef.current = true;
    }
    setModalTask((prev) => ({ ...prev, lastDone: value }));
    setModalErrors((prev) => {
      if (!prev.lastDone) return prev;
      const next = { ...prev };
      delete next.lastDone;
      return next;
    });
  };


  const handleOpenTaskModal = (task = {}) => {
    defaultMeterAppliedRef.current = false;
    resetCycleState();
    setModalErrors({});
    setIsLastDoneLocked(true);
    setShowLastDoneWarning(false);
    setLatestMeterReading(null);
    const defaultSchedulingGroupId = schedulingGroupOptions.find(g => g.name === 'A')?.id || null;
    const lastDoneValue =
      Number(task.calendarBased ?? 1) === 1
        ? toDateInputValue(task.lastDoneDate)
        : task.lastDone !== undefined && task.lastDone !== null
          ? String(task.lastDone)
          : '';
    initialLastDoneRef.current = lastDoneValue;
    lastDoneEditedRef.current = false;
    const initialCodeName =
      toTrimmedString(task?.code?.name) ||
      toTrimmedString(task?.taskCode?.name) ||
      '';
    setSelectedTaskCodeName(initialCodeName);
    const resolvedRotablesId = normalizeRotablesIdValue(
      task.rotablesId ?? task.rotable?.id ?? task.rotableId ?? null
    );
    initialRotablesIdRef.current = resolvedRotablesId;
    const normalizedSchedulingType =
      task.schedulingType === 'cycle' || task.schedulingType === 'dependency'
        ? 'cycle'
        : task.schedulingType || 'simple';

    const newTask = {
      ...task,
      codeId: task.codeId,
      modifierCodeId: task.modifierCodeId,
      taskTypeId: task.taskType?.id || '',
      // taskId: task.assetTask?.id || '',
      calendarBased: task.calendarBased ?? 1,
      frequency: task.frequency ?? 0,
      assetMeterId: task.assetMeterId?.id || task.assetMeterId || null,
      rotablesId: resolvedRotablesId,
      rotableTransactionNotes: '',
      rotableTransactionFiles: [],
      rotableTransactionImages: [],
      statutory: task.statutory ?? 0,
      planningTask: task.planningTask ?? 0,
      schedulingType: normalizedSchedulingType,
      forecastBasis: task.forecastBasis ?? 'lastdone',
      schedulingGroupId: task.schedulingGroupId || task.schedulingGroup?.id || null,
      schedulingWindow: task.schedulingWindow ?? 48,
      lastDone: lastDoneValue,
      schedulingPriority: task.schedulingPriority ?? 1,
      formIds: Array.isArray(task.formIds)
        ? task.formIds
          .map((id) => Number(id))
          .filter((id) => Number.isInteger(id) && id > 0)
        : [],
    };
    const resolvedCostResponsibilityId =
      task.costResponsibilityId ?? task.costResponsibility?.id ?? null;
    if (
      resolvedCostResponsibilityId !== null &&
      resolvedCostResponsibilityId !== undefined
    ) {
      newTask.costResponsibilityId = resolvedCostResponsibilityId;
    }
    if (newTask.costResponsibilityId === undefined || newTask.costResponsibilityId === null) {
      newTask.costResponsibilityId = 1;
    }
    if (!task.id) {
      newTask.partsExpense = parseInt(defaultExpenseParts, 10);
      newTask.labourExpense = parseInt(defaultExpenseLabour, 10);
      newTask.miscExpense = parseInt(defaultExpenseMisc, 10);
      newTask.miscPrice = 0;
      newTask.defaultCostCentre =
        asset?.costCenter?.id || asset?.costCenterId || null;
      newTask.planningHorizon = 3; // 1 Month (30 days)
      newTask.taskFiles = [];
      newTask.formIds = [];
      if (!newTask.schedulingGroupId) newTask.schedulingGroupId = defaultSchedulingGroupId;
      if (hasAssetMeters) {
        newTask.calendarBased = 0;
        newTask.planningTask = 1;
        if (!newTask.assetMeterId && primaryAssetMeter) {
          newTask.assetMeterId = primaryAssetMeter.id;
        }
        defaultMeterAppliedRef.current = true;
      }
    } else {
      newTask.taskFiles = Array.isArray(task.taskFiles)
        ? task.taskFiles.map((f) =>
          typeof f === 'string' ? { name: f, filename: f } : f,
        )
        : [];
    }
    setModalTask(newTask);
    if (task.id) {
      (async () => {
        try {
          const mats = await getBillOfMaterialsByTaskId(task.id);
          const mappedMats = mats.map(m => ({
            ...m,
            parts: {
              partNumber: m.partNumber,
              description: m.partDescription,
              lastPrice: m.lastPrice,
              pricingDate: m.pricingDate,
            },
            partsPrice: m.partsPrice,
            pricingDate: m.pricingDate,
          }));
          const equip = await getBillOfEquipmentByTaskId(task.id);
          const lab = await getBillOfLabourByTaskId(task.id);
          const serv = await getBillOfServicesByTaskId(task.id);
          setTaskMaterials(mappedMats);
          setTaskEquipment(equip);
          setTaskLabour(lab);
          setTaskServices(serv);
          const total = computePartsTotal(mappedMats);
          const labourTotal = computeLabourTotal(lab);
          const serviceTotal = computeServicesTotal(serv);
          setPartsOverride(Boolean(task.partsOverride));
          setLabourOverride(Boolean(task.labourOverride));
          setServicesOverride(Boolean(task.servicesOverride));
          setModalTask(prev => ({
            ...prev,
            partsPrice: task.partsOverride ? task.partsPrice : total,
            labourCost: task.labourOverride ? task.labourCost : labourTotal,
            miscPrice: task.servicesOverride ? task.miscPrice : serviceTotal,
          }));
        } catch (err) {
          console.error('Error loading task details', err);
          setTaskMaterials([]);
          setTaskEquipment([]);
          setTaskLabour([]);
          setTaskServices([]);
        }
        setShowModal(true);
      })();
    } else {
      setTaskMaterials([]);
      setTaskEquipment([]);
      setTaskLabour([]);
      setTaskServices([]);
      setPartsOverride(false);
      setLabourOverride(false);
      setServicesOverride(false);
      setShowModal(true);
    }
  };


  const taskFormFilters = useMemo(() => {
    const departmentId = Number(asset?.assetDepartmentId);
    const classId = Number(asset?.group?.classId);
    const groupId = Number(asset?.groupId);

    return {
      departmentIds: Number.isInteger(departmentId) && departmentId > 0 ? [departmentId] : undefined,
      classIds: Number.isInteger(classId) && classId > 0 ? [classId] : undefined,
      groupIds: Number.isInteger(groupId) && groupId > 0 ? [groupId] : undefined,
    };
  }, [asset?.assetDepartmentId, asset?.group?.classId, asset?.groupId]);

  const loadTaskForms = useCallback(async () => {
    try {
      const forms = await getForms(taskFormFilters);
      setTaskFormOptions(Array.isArray(forms) ? forms : []);
      return true;
    } catch (error) {
      console.error('Error loading forms for task', error);
      showToastError('Unable to load forms.');
      return false;
    }
  }, [showToastError, taskFormFilters]);

  const selectedTaskForms = useMemo(() => {
    const ids = Array.isArray(modalTask?.formIds) ? modalTask.formIds : [];
    return ids
      .map((id) => Number(id))
      .filter((id) => Number.isInteger(id) && id > 0)
      .map((id) => {
        const match = taskFormOptions.find((form) => Number(form.id) === id);
        return { id, name: match?.name || `Form ${id}` };
      });
  }, [modalTask?.formIds, taskFormOptions]);

  const getTaskFormInputTypeById = useCallback(
    (id) => taskFormInputTypes.find((inputType) => inputType.id === Number(id))?.name || '',
    [taskFormInputTypes],
  );

  const renderTaskFormPreviewInput = useCallback(
    (item) => previewRenderer(item, getTaskFormInputTypeById),
    [getTaskFormInputTypeById],
  );

  const handleTaskFormsTabEnter = useCallback(() => {
    loadTaskForms();
  }, [loadTaskForms]);

  const openAddTaskFormModal = useCallback(async () => {
    const loaded = await loadTaskForms();
    if (!loaded) {
      return;
    }

    setSelectedTaskFormId('');
    setShowTaskFormModal(true);
  }, [loadTaskForms]);

  const handleSaveTaskFormSelection = useCallback(() => {
    const parsed = Number(selectedTaskFormId);
    if (!Number.isInteger(parsed) || parsed <= 0) return;

    setModalTask((prev) => {
      if (!prev) return prev;
      const currentIds = Array.isArray(prev.formIds)
        ? prev.formIds
          .map((id) => Number(id))
          .filter((id) => Number.isInteger(id) && id > 0)
        : [];
      if (currentIds.includes(parsed)) {
        return prev;
      }
      return { ...prev, formIds: [...currentIds, parsed] };
    });
    setShowTaskFormModal(false);
    setSelectedTaskFormId('');
  }, [selectedTaskFormId]);

  const handleRemoveTaskForm = useCallback((formId) => {
    setModalTask((prev) => {
      if (!prev) return prev;
      const currentIds = Array.isArray(prev.formIds) ? prev.formIds : [];
      return {
        ...prev,
        formIds: currentIds.filter((id) => Number(id) !== Number(formId)),
      };
    });
  }, []);

  const handleViewTaskForm = useCallback(async (formId) => {
    try {
      setTaskFormPreviewLoading(true);
      setShowTaskFormPreviewModal(true);
      const [inputTypes, form] = await Promise.all([
        getFormInputType(),
        getFormById(Number(formId)),
      ]);
      setTaskFormInputTypes(Array.isArray(inputTypes) ? inputTypes : []);
      setPreviewTaskForm(form || null);
    } catch (error) {
      console.error('Error loading form preview', error);
      showToastError('Unable to load form preview.');
      setShowTaskFormPreviewModal(false);
    } finally {
      setTaskFormPreviewLoading(false);
    }
  }, [showToastError]);

  const handleModalSave = async () => {
    const isFormValid = validateModalTaskForm();
    const errors = {};

    if (modalTask.partsPrice !== undefined && Number(modalTask.partsPrice) < 0) {
      errors.partsPrice = 'Total Parts Cost cannot be negative';
    }
    if (modalTask.labourCost !== undefined && Number(modalTask.labourCost) < 0) {
      errors.labourCost = 'Total Labour Cost cannot be negative';
    }
    if (modalTask.miscPrice !== undefined && Number(modalTask.miscPrice) < 0) {
      errors.miscPrice = 'Total Other Cost cannot be negative';
    }

    const rawLastDone = modalTask?.lastDone;

    if (
      Number(modalTask?.calendarBased) !== 1 &&
      rawLastDone !== undefined &&
      rawLastDone !== null &&
      String(rawLastDone).trim() !== ''
    ) {
      const parsedLastDone = Number(rawLastDone);

      // Must be a valid number and non negative
      if (Number.isNaN(parsedLastDone) || parsedLastDone < 0) {
        errors.lastDone = 'Last Known Change must be a non negative number';
      } else {
        // SQL decimal(10,2) style limit: total digits max 10
        const numericString = parsedLastDone.toString().replace('.', '');
        if (numericString.length > 10) {
          errors.lastDone = 'Last Known Change exceeds the 10 digit precision limit';
        }

        // Compare against latest meter reading if relevant
        if (
          typeof latestMeterReading === 'number' &&
          Number.isFinite(latestMeterReading) &&
          latestMeterReading > 0 &&
          parsedLastDone >= latestMeterReading
        ) {
          errors.lastDone = `Last Known Change must be less than the latest meter reading (${latestMeterReading})`;
        }
      }
    }

    const freqError = validateFrequency(modalTask);
    if (freqError) {
      errors.frequency = freqError;
    }

    if (Object.keys(errors).length > 0) {
      setModalErrors((prev) => ({ ...prev, ...errors }));
    }

    if (!isFormValid || Object.keys(errors).length > 0) return;
    const isCycleValid = validateCycleConfiguration();
    if (!isCycleValid) return;
    const payload = Object.fromEntries(
      Object.entries(modalTask).filter(
        ([key, value]) =>
          typeof value !== 'object' || value === null || key === 'taskFiles' || key === 'formIds'
      )
    );

    payload.assetId = assetId;
    payload.partsOverride = partsOverride ? 1 : 0;
    payload.labourOverride = labourOverride ? 1 : 0;
    payload.servicesOverride = servicesOverride ? 1 : 0;
    payload.formIds = Array.isArray(modalTask?.formIds)
      ? modalTask.formIds
        .map((id) => Number(id))
        .filter((id) => Number.isInteger(id) && id > 0)
      : [];
    payload.codeId = payload.codeId;
    payload.modifierCodeId = payload.modifierCodeId;
    if (Number(modalTask?.calendarBased) === 1) {
      payload.lastDoneDate = rawLastDone ? new Date(rawLastDone).toISOString() : null;
      delete payload.lastDone;
    } else {
      delete payload.lastDoneDate;
      payload.lastDone = rawLastDone || 0;
    }

    const cleanedPayload = { ...payload };
    const lastDoneChanged = String(rawLastDone ?? '') !== String(initialLastDoneRef.current ?? '');
    if (!isNewModalTask && lastDoneEditedRef.current && lastDoneChanged) {
      cleanedPayload.lastDoneOverride = 0;
    }

    [
      'code',
      'codeLabel',
      'modifierLabel',
      'assetNumber',
      'assetDescription',
      'assetSerial',
      'cycleGroupName',
      'componentEndValue',
      'componentRisk',
      'componentLifeRatio',
      'componentOpportunity',
      'componentEndLifeRemain',
      'costCenter',
      'costToDate',
      'endUsage',
      'forecastCosts',
      'forecastOccurrences',
      'labourExpense2',
      'lastChangedDate',
      'lastChangedReading',
      'lastDonePerSchedule',
      'lifeRemaining',
      'lifeToDate',
      'maintenancePlan',
      'miscExpense2',
      'modifier',
      'modifierTask',
      'nextOccurrenceDate',
      'nextOccurrenceUsage',
      'nextServiceTask',
      'partsExpense2',
      'schedulingGroup',
      'schedulingGroupFrequency',
      'taskPlanningHorizon',
      'taskReadingEstimate',
      'taskType',
      'usageOccurrences',
      'workOrders',
      'rotableTransactionNotes',
      'totalPartsCost',
      'totalLabourCost',
      'totalMiscCost',
      'totalLabourHours',
      'totalTaskLccCost',
    ].forEach(field => delete cleanedPayload[field]);

    const cycleConfigurationPayload = buildCycleConfigurationPayload();
    if (cycleConfigurationPayload) {
      cleanedPayload.cycleConfiguration = cycleConfigurationPayload;
    }

    let savedId = modalTask.id;
    if (isNewModalTask) {
      const created = await createTasks(cleanedPayload);
      savedId = created.id;
    } else {
      await updateTasks(modalTask.id, cleanedPayload);
    }

    const resolvedRotablesId = normalizeRotablesIdValue(cleanedPayload.rotablesId);
    const hasRotableTransactionDetails =
      Boolean(modalTask?.rotableTransactionNotes?.trim()) ||
      (Array.isArray(modalTask?.rotableTransactionFiles) &&
        modalTask.rotableTransactionFiles.length > 0) ||
      (Array.isArray(modalTask?.rotableTransactionImages) &&
        modalTask.rotableTransactionImages.length > 0);
    const rotableSelectionChanged =
      resolvedRotablesId &&
      String(resolvedRotablesId) !== String(initialRotablesIdRef.current ?? '');

    if (
      useRotables &&
      resolvedRotablesId &&
      (isNewModalTask || rotableSelectionChanged || hasRotableTransactionDetails)
    ) {
      try {
        await fitRotableToAsset(resolvedRotablesId, {
          notes: modalTask?.rotableTransactionNotes || '',
          files: modalTask?.rotableTransactionFiles || [],
          images: modalTask?.rotableTransactionImages || [],
          assetId,
          taskId: savedId,
        });
      } catch (err) {
        console.error('Error creating rotable fit transaction', err);
      }
    }

    await Promise.all(
      taskMaterials.map((m) => {
        const matPayload = {
          taskId: savedId,
          partsId: m.partsId,
          optional: m?.optional || 0,
          quantity: m.quantity,
          defaultExpenseCode: m.defaultExpenseCode ?? defaultExpenseParts,
          partsPrice: m.partsPrice,
          pricingDate: m.pricingDate || new Date().toISOString(),
        };
        return m.id
          ? updateBillOfMaterials(m.id, matPayload)
          : createBillOfMaterials(matPayload);
      })
    );
    await Promise.all(
      taskEquipment.map(e =>
        e.id ? updateBillOfEquipment(e.id, e) : createBillOfEquipment({ ...e, taskId: savedId })
      )
    );
    await Promise.all(
      taskLabour.map(l =>
        l.id ? updateBillOfLabour(l.id, l) : createBillOfLabour({ ...l, taskId: savedId })
      )
    );
    await Promise.all(
      taskServices.map(s =>
        s.id ? updateBillOfServices(s.id, s) : createBillOfServices({ ...s, taskId: savedId })
      )
    );
    await Promise.all(deletedMaterialIds.map(id => deleteBillOfMaterials(id)));
    await Promise.all(deletedEquipmentIds.map(id => deleteBillOfEquipment(id)));
    await Promise.all(deletedLabourIds.map(id => deleteBillOfLabour(id)));
    await Promise.all(deletedServiceIds.map(id => deleteBillOfServices(id)));

    resetCycleState();
    setShowModal(false);
    setIsLastDoneLocked(true);
    setShowLastDoneWarning(false);
    setLatestMeterReading(null);
    setPartsOverride(false);
    setLabourOverride(false);
    setServicesOverride(false);
    setModalTask(null);
    lastDoneEditedRef.current = false;
    initialLastDoneRef.current = '';
    initialRotablesIdRef.current = null;
    setDeletedMaterialIds([]);
    setDeletedEquipmentIds([]);
    setDeletedLabourIds([]);
    setDeletedServiceIds([]);
    await loadTasksByAsset(assetId);
    // setTasks({});
  };

  const handlePartModalSave = () => {
    const errs = validateForm(partModalFields, partForm);
    const hasQuantityValue =
      partForm.quantity !== undefined && partForm.quantity !== null && partForm.quantity !== '';
    if (
      hasQuantityValue &&
      (!Number.isInteger(Number(partForm.quantity)) || Number(partForm.quantity) < 0)
    ) {
      errs.quantity = 'Quantity must be a positive whole number or zero';
    }
    const hasPriceValue =
      partForm.partsPrice !== undefined &&
      partForm.partsPrice !== null &&
      partForm.partsPrice !== '';
    const parsedPrice = hasPriceValue ? Number(partForm.partsPrice) : 0;
    if (Number.isNaN(parsedPrice)) {
      errs.partsPrice = 'Parts Cost must be a valid number';
    } else if (parsedPrice < 0) {
      errs.partsPrice = 'Parts Cost cannot be negative';
    }
    if (!partForm.pricingDate || isNaN(new Date(partForm.pricingDate).getTime())) {
      errs.pricingDate = 'Pricing Date must be a valid date';
    }
    if (!partForm.defaultExpenseCode) {
      errs.defaultExpenseCode = 'Expense Code is required';
    }
    setPartModalErrors(errs);
    if (Object.keys(errs).length > 0) return;
    const normalizedPartsPrice = Number.isNaN(parsedPrice) ? 0 : parsedPrice;
    const selectedPartData = selectedPartOption?.raw || partForm.parts || null;
    const selectedPartId = selectedPartOption?.value ?? partForm.partsId ?? selectedPartData?.id ?? null;
    const partNumber = selectedPartData?.partNumber ?? partForm.partNumber ?? '';
    const partDescription = selectedPartData?.description ?? partForm.partDescription ?? '';
    const normalizedPartData = selectedPartData ? { ...selectedPartData } : null;
    const formData = {
      ...partForm,
      partsId: selectedPartId,
      parts: normalizedPartData,
      partNumber,
      partDescription,
      partsPrice: hasPriceValue ? normalizedPartsPrice : 0,
      pricingDate: partForm.pricingDate ?? normalizedPartData?.pricingDate ?? null,
      defaultExpenseCode: partForm.defaultExpenseCode ?? defaultExpenseParts,
    };
    let newMaterials;
    if (editingPartIndex !== null) {
      newMaterials = taskMaterials.map((m, i) => (i === editingPartIndex ? formData : m));
    } else {
      newMaterials = [...taskMaterials, formData];
    }
    setTaskMaterials(newMaterials);
    if (!partsOverride) {
      const total = computePartsTotal(newMaterials);
      setModalTask((prev) => ({ ...prev, partsPrice: total }));
    }
    setShowPartModal(false);
    setPartModalErrors({});
    setEditingPartIndex(null);
    setPartForm({});
    setSelectedPartOption(null);
  };

  const handlePartCreatedFromModal = useCallback(
    ({ part, partsCost = '', pricingDate }) => {
      if (!part || !part.id) return;

      const resolvedPrice =
        partsCost !== undefined && partsCost !== null && partsCost !== ''
          ? partsCost
          : part.partsCost ?? part.lastPrice ?? '';
      const resolvedPricingDate = pricingDate || part.pricingDate || '';
      const optionPayload = {
        value: part.id,
        label: formatPartOptionLabel(part.partNumber, part.description) || `Part ${part.id}`,
        raw: part,
      };

      setSelectedPartOption(optionPayload);
      setPartForm((prev) => ({
        ...prev,
        partsId: part.id,
        parts: part,
        partNumber: part.partNumber ?? '',
        partDescription: part.description ?? '',
        partsPrice: resolvedPrice,
        pricingDate: resolvedPricingDate ? toDateInputValue(resolvedPricingDate) : prev.pricingDate,
        defaultExpenseCode: prev.defaultExpenseCode ?? defaultExpenseParts,
      }));
      setPartSelectRefreshKey((prev) => prev + 1);
      setShowCreatePartModal(false);
    },
    [defaultExpenseParts, formatPartOptionLabel],
  );

  const handleEquipmentModalSave = () => {
    const errs = validateForm(equipmentModalFields, equipmentForm);
    if (!equipmentForm.name || equipmentForm.name.length < 3) {
      errs.name = 'Name must be at least 3 characters';
    }
    if (!equipmentForm.quantity || Number(equipmentForm.quantity) <= 0 || !Number.isInteger(Number(equipmentForm.quantity))) {
      errs.quantity = 'Quantity must be a positive whole number';
    }
    setEquipmentModalErrors(errs);
    if (Object.keys(errs).length > 0) return;
    if (editingEquipmentIndex !== null) {
      setTaskEquipment((prev) => prev.map((e, i) => (i === editingEquipmentIndex ? equipmentForm : e)));
    } else {
      setTaskEquipment((prev) => [...prev, equipmentForm]);
    }
    setShowEquipmentModal(false);
    setEquipmentModalErrors({});
  };

  const handlePartModalClose = () => {
    setShowPartModal(false);
    setPartModalErrors({});
    setEditingPartIndex(null);
    setPartForm({});
    setSelectedPartOption(null);
  };

  const handleLabourModalSave = () => {
    const errs = validateForm(labourModalFields, labourForm);
    if (!labourForm.qty || Number(labourForm.qty) <= 0 || !Number.isInteger(Number(labourForm.qty))) {
      errs.qty = 'Quantity must be a positive whole number';
    }
    if (!labourForm.labourHours || Number(labourForm.labourHours) <= 0) {
      errs.labourHours = 'Hours must be a positive number';
    }
    if (!labourForm.labourRate || Number(labourForm.labourRate) <= 0) {
      errs.labourRate = 'Hourly rate must be a positive number';
    }
    if (!labourForm.defaultExpenseCode) {
      errs.defaultExpenseCode = 'Expense Code is required';
    }
    setLabourModalErrors(errs);
    if (Object.keys(errs).length > 0) return;
    let newLabour;
    if (editingLabourIndex !== null) {
      newLabour = taskLabour.map((l, i) => (i === editingLabourIndex ? labourForm : l));
    } else {
      newLabour = [...taskLabour, labourForm];
    }
    setTaskLabour(newLabour);
    if (!labourOverride) {
      const total = computeLabourTotal(newLabour);
      setModalTask((prev) => ({ ...prev, labourCost: total }));
    }
    setShowLabourModal(false);
    setLabourModalErrors({});
  };

  const handleServiceModalSave = () => {
    const errs = validateForm(serviceModalFields, serviceForm);
    if (!serviceForm.quantity || Number(serviceForm.quantity) <= 0 || !Number.isInteger(Number(serviceForm.quantity))) {
      errs.quantity = 'Quantity must be a positive whole number';
    }
    if (!serviceForm.cost || Number(serviceForm.cost) <= 0) {
      errs.cost = 'Cost must be a positive number';
    }
    if (!serviceForm.defaultExpenseCode) {
      errs.defaultExpenseCode = 'Expense Code is required';
    }
    setServiceModalErrors(errs);
    if (Object.keys(errs).length > 0) return;
    let newServices;
    if (editingServiceIndex !== null) {
      newServices = taskServices.map((s, i) => (i === editingServiceIndex ? serviceForm : s));
    } else {
      newServices = [...taskServices, serviceForm];
    }
    setTaskServices(newServices);
    if (!servicesOverride) {
      const total = computeServicesTotal(newServices);
      setModalTask((prev) => ({ ...prev, miscPrice: total }));
    }
    setShowServiceModal(false);
    setServiceModalErrors({});
  };

  useEffect(() => {
    // Load dropdowns
    const fetchDropdowns = async () => {
      try {
        const [taskTypeRes, unitRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/task-types`, {
            credentials: 'include',
          }),
          fetch(`${process.env.REACT_APP_API_URL}/measurement-units`, {
            credentials: 'include',
          }),
        ]);

        const taskTypesData = await taskTypeRes.json();
        const unitsData = await unitRes.json();

        setTaskTypes(taskTypesData);
        setUnits(unitsData);
      } catch (err) {
        console.error('Failed to load dropdowns', err);
      }
    };

    fetchDropdowns();
    getAssetMeter()
      .then((data) => setAssetMeters(Array.isArray(data) ? data : []))
      .catch((err) => console.error('asset-meter', err));
    fetchData(getTrades, {}, setTradeOptions);
    fetchData(getExpenseCodes, {}, setExpenseOptions);
    fetchData(getExternalServices, {}, setServiceOptions);
    fetchData(getSchedulingGroups, {}, setSchedulingGroupOptions);
    fetchData(getWorkOrderStatus, {}, setWorkOrderStatuses);
  }, [assetId]);

  const handleFileDelete = async (filename, idx) => {
    if (!window.confirm('Delete this file?')) return;

    const currentFiles = Array.isArray(modalTask.taskFiles) ? modalTask.taskFiles : [];
    const fileEntry = currentFiles[idx];
    const { key: derivedKey, url: derivedUrl } = extractDeletionTargets(fileEntry, filename);
    const normalizedFilename = normalizeFileKey(filename);
    const deletionKey = derivedKey || normalizedFilename || filename;

    setModalTask((prev) => {
      const nextFiles = Array.isArray(prev.taskFiles)
        ? prev.taskFiles.filter((_, i) => i !== idx)
        : prev.taskFiles;

      const nextState = {
        ...prev,
        taskFiles: nextFiles,
      };

      const urlProps = ['taskFileUrls', 'taskFilesUrls'];
      urlProps.forEach((prop) => {
        if (Array.isArray(prev[prop])) {
          const filtered = removeMatchingUrls(prev[prop], deletionKey ? normalizeFileKey(deletionKey) : '', derivedUrl);
          if (filtered !== prev[prop]) {
            nextState[prop] = filtered;
          }
        }
      });

      return nextState;
    });

    if (!deletionKey) return;

    try {
      await deleteUploadedFile(deletionKey, 'taskfiles');
    } catch (err) {
      console.error('Error deleting file', err);
    }
  };

  const handleRotableTransactionFileDelete = async (filename, idx) => {
    if (!window.confirm('Delete this file?')) return;
    const currentFiles = Array.isArray(modalTask?.rotableTransactionFiles)
      ? modalTask.rotableTransactionFiles
      : [];
    const fileEntry = currentFiles[idx];
    const { key: derivedKey } = extractDeletionTargets(fileEntry, filename);
    const normalizedFilename = normalizeFileKey(filename);
    const deletionKey = derivedKey || normalizedFilename || filename;

    setModalTask((prev) => ({
      ...prev,
      rotableTransactionFiles: Array.isArray(prev.rotableTransactionFiles)
        ? prev.rotableTransactionFiles.filter((_, i) => i !== idx)
        : prev.rotableTransactionFiles,
    }));

    if (!deletionKey) return;

    try {
      await deleteUploadedFile(deletionKey, 'partsfiles');
    } catch (err) {
      console.error('Error deleting rotable transaction file', err);
    }
  };

  const handleInlineSave = async () => {
    const freqError = validateFrequency(editRow);
    if (freqError) {
      alert(freqError);
      return;
    }
    const codeId = editRow.codeId;
    if (!codeId) {
      alert('Select a task code.');
      return;
    }
    const modifierCodeId = editRow.modifierCodeId;
    const payload = {
      name: editRow.name,
      codeId,
      modifierCodeId,
      frequency: editRow.frequency,
      taskTypeId: editRow.taskTypeId,
      assetMeterId: editRow.assetMeterId,
      assetId
    };
    if (editTaskId === 'new') {
      await createTasks(payload);
    } else {
      await updateTasks(editTaskId, payload);
    }

    setEditTaskId(null);
    setEditRow({});
    await loadTasksByAsset(assetId);
  };

  const normalizeBomOptional = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'boolean') return value ? 1 : 0;
    if (typeof value === 'number') return value ? 1 : 0;
    if (typeof value === 'string') {
      const trimmed = value.trim().toLowerCase();
      if (['1', 'true', 'yes', 'y'].includes(trimmed)) return 1;
      if (['0', 'false', 'no', 'n'].includes(trimmed)) return 0;
    }
    return 0;
  };

  const normalizeBomQuantity = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) return 1;
    return Math.trunc(parsed);
  };

  const normalizeBomPrice = (value) => {
    if (value === null || value === undefined || value === '') return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const normalizeBomPricingDate = (value) => {
    if (!value) return undefined;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
  };

  const findExistingPartByNumber = async (partNumber) => {
    const trimmed = typeof partNumber === 'string' ? partNumber.trim() : '';
    if (!trimmed) return null;
    const response = await getParts({ search: trimmed, page: 1, pageSize: 25 });
    const items = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response)
        ? response
        : [];
    const lower = trimmed.toLowerCase();
    return items.find((item) => item.partNumber?.toLowerCase() === lower) || null;
  };

  const openBomAiModal = () => {
    setBomAiText('');
    setBomAiError('');
    setShowBomAiModal(true);
  };

  const closeBomAiModal = () => {
    if (bomAiLoading) return;
    setShowBomAiModal(false);
    setBomAiText('');
    setBomAiError('');
  };

  const handleImportBomWithAi = async () => {
    if (bomAiLoading) return;
    if (!bomAiText.trim()) {
      setBomAiError('Please provide bill of materials details.');
      return;
    }
    setBomAiLoading(true);
    setBomAiError('');

    try {
      const response = await createBillOfMaterialsWithAi(bomAiText.trim());
      if (response?.message) {
        setBomAiError(response.message);
        return;
      }

      const aiParts = Array.isArray(response?.data) ? response.data : null;
      if (!aiParts) {
        setBomAiError('Unable to parse AI response.');
        return;
      }

      const normalizedParts = aiParts
        .map((part) => ({
          partNumber: typeof part.partNumber === 'string' ? part.partNumber.trim() : '',
          partDescription: typeof part.partDescription === 'string' ? part.partDescription.trim() : '',
          quantity: normalizeBomQuantity(part.quantity),
          partsPrice: normalizeBomPrice(part.partsPrice),
          pricingDate: normalizeBomPricingDate(part.pricingDate),
          optional: normalizeBomOptional(part.optional),
        }))
        .filter((part) => part.partNumber);

      const lookupResults = await Promise.all(
        normalizedParts.map(async (part) => ({
          part,
          existingPart: await findExistingPartByNumber(part.partNumber),
        })),
      );

      const existingMaterials = lookupResults
        .filter((result) => result.existingPart)
        .map(({ part, existingPart }) => ({
          partsId: existingPart.id,
          parts: existingPart,
          partNumber: existingPart.partNumber ?? part.partNumber,
          partDescription: existingPart.description ?? part.partDescription,
          quantity: part.quantity,
          optional: part.optional,
          partsPrice:
            part.partsPrice !== undefined && part.partsPrice !== null
              ? part.partsPrice
              : existingPart.lastPrice ?? 0,
          pricingDate: part.pricingDate ?? existingPart.pricingDate ?? new Date().toISOString(),
          defaultExpenseCode: defaultExpenseParts,
        }));

      const missingParts = lookupResults
        .filter((result) => !result.existingPart)
        .map(({ part }) => ({
          ...part,
          sourceOfSupplyId: '',
          supplierId: '',
        }));

      if (existingMaterials.length > 0) {
        setTaskMaterials((prev) => {
          const updated = [...prev, ...existingMaterials];
          if (!partsOverride) {
            setModalTask((prevTask) => ({ ...prevTask, partsPrice: computePartsTotal(updated) }));
          }
          return updated;
        });
      }

      if (missingParts.length > 0) {
        setMissingBomParts(missingParts);
        setShowMissingBomModal(true);
        setMissingBomError('');
        setBomApplySourceToAll(false);
        setBomApplySupplierToAll(false);
        setBomGlobalSourceId('');
        setBomGlobalSupplierId('');
      }

      setShowBomAiModal(false);
      setBomAiText('');
    } catch (error) {
      console.error('Error importing BOM with AI:', error);
      setBomAiError('Unable to create bill of materials with AI.');
    } finally {
      setBomAiLoading(false);
    }
  };

  const handleMissingBomUpdate = (index, updates) => {
    setMissingBomParts((prev) =>
      prev.map((part, i) => (i === index ? { ...part, ...updates } : part)),
    );
  };

  const handleGlobalBomSourceChange = (value) => {
    setBomGlobalSourceId(value);
    if (bomApplySourceToAll) {
      setMissingBomParts((prev) =>
        prev.map((part) => ({ ...part, sourceOfSupplyId: value })),
      );
    }
  };

  const handleGlobalBomSupplierChange = (value) => {
    setBomGlobalSupplierId(value);
    if (bomApplySupplierToAll) {
      setMissingBomParts((prev) =>
        prev.map((part) => ({ ...part, supplierId: value })),
      );
    }
  };

  const handleApplySourceToAllChange = (checked) => {
    setBomApplySourceToAll(checked);
    if (checked) {
      setMissingBomParts((prev) =>
        prev.map((part) => ({ ...part, sourceOfSupplyId: bomGlobalSourceId })),
      );
    }
  };

  const handleApplySupplierToAllChange = (checked) => {
    setBomApplySupplierToAll(checked);
    if (checked) {
      setMissingBomParts((prev) =>
        prev.map((part) => ({ ...part, supplierId: bomGlobalSupplierId })),
      );
    }
  };

  const handleSaveMissingBomParts = async () => {
    if (missingBomSaving) return;
    const incomplete = missingBomParts.some(
      (part) => !part.sourceOfSupplyId || !part.supplierId,
    );
    if (incomplete) {
      setMissingBomError(
        'Please select a Source of Supply and Preferred Supplier for all parts.',
      );
      return;
    }

    setMissingBomSaving(true);
    setMissingBomError('');

    try {
      const createdParts = await Promise.all(
        missingBomParts.map(async (part) =>
          createParts({
            partNumber: part.partNumber,
            description: part.partDescription,
            sourceOfSupplyId: Number(part.sourceOfSupplyId),
            supplierId: Number(part.supplierId),
            categoryId: DEFAULT_PART_CATEGORY_ID,
            lastPrice: part.partsPrice ?? '',
          }),
        ),
      );

      const newMaterials = createdParts.map((created, index) => {
        const aiPart = missingBomParts[index];
        return {
          partsId: created.id,
          parts: created,
          partNumber: created.partNumber ?? aiPart.partNumber,
          partDescription: created.description ?? aiPart.partDescription,
          quantity: aiPart.quantity,
          optional: aiPart.optional,
          partsPrice:
            aiPart.partsPrice !== undefined && aiPart.partsPrice !== null
              ? aiPart.partsPrice
              : created.lastPrice ?? 0,
          pricingDate: aiPart.pricingDate ?? created.pricingDate ?? new Date().toISOString(),
          defaultExpenseCode: defaultExpenseParts,
        };
      });

      setTaskMaterials((prev) => {
        const updated = [...prev, ...newMaterials];
        if (!partsOverride) {
          setModalTask((prevTask) => ({ ...prevTask, partsPrice: computePartsTotal(updated) }));
        }
        return updated;
      });

      setShowMissingBomModal(false);
      setMissingBomParts([]);
      setMissingBomError('');
    } catch (error) {
      console.error('Error saving BOM parts:', error);
      setMissingBomError('Unable to add new parts. Please try again.');
    } finally {
      setMissingBomSaving(false);
    }
  };

  const handleShowOccurrences = async (task) => {
    if (task.frequency === 0 || null || '') return; // test zero of empty frequency to avoid infinite loop
    try {
      const taskOccurrences = [task.usageOccurrences];
      const measurementUnits = task.calendarBased === 1 ? 'Days' : task.assetMeter?.meter?.measurementUnits?.unitName || 'Days';
      const occurrenceTitle = task.cycleGroupId ? task?.cycleGroupName : `${task?.name} Occurrences` || 'Task Occurrences';

      taskOccurrences.push(
        {
          measurementUnits: measurementUnits,
          title: occurrenceTitle,
          isCycleGroup: task?.cycleGroupId ? 1 : 0
        },
        task
      );

      setTaskOccurrences(taskOccurrences);
      setShowOccurrencesModal(true);
    } catch (err) {
      console.error('Error loading occurrences', err);
    }
  };

  const fetchWorkOrderDetails = useCallback(async (workOrderId) => {
    if (!workOrderId) return;
    setWorkOrderDetailsLoading(true);
    try {
      const workOrder = await getWorkOrderById(workOrderId);
      setWorkOrderDetails(workOrder);
      const [parts, equipment, labour, services, forms] = await Promise.all([
        getWorkOrderPartsByWorkOrderId(workOrderId),
        getBillOfEquipmentWosByWorkOrderId(workOrderId),
        getBillOfLabourWosByWorkOrderId(workOrderId),
        getBillOfServicesWosByWorkOrderId(workOrderId),
        getWorkOrderFormsByWorkOrderId(workOrderId),
      ]);
      setWorkOrderParts(parts);
      setWorkOrderEquipment(equipment);
      setWorkOrderLabour(labour);
      setWorkOrderServices(services);
      setWorkOrderForms(forms);
      setShowWorkOrderDetailsModal(true);
    } catch (error) {
      console.error('Error loading work order details', error);
      setWorkOrderDetails(null);
      setWorkOrderParts([]);
      setWorkOrderEquipment([]);
      setWorkOrderLabour([]);
      setWorkOrderServices([]);
      setWorkOrderForms([]);
    } finally {
      setWorkOrderDetailsLoading(false);
    }
  }, []);

  const handleWorkOrderLinkClick = useCallback(async (workOrder) => {
    if (!workOrder) return;
    // setSelectedWorkOrder(workOrder);
    await fetchWorkOrderDetails(workOrder);
  }, [fetchWorkOrderDetails]);

  const handleCloseWorkOrderDetails = () => {
    setShowWorkOrderDetailsModal(false);
    setSelectedWorkOrder(null);
    setWorkOrderDetails(null);
    setWorkOrderParts([]);
    setWorkOrderEquipment([]);
    setWorkOrderLabour([]);
    setWorkOrderServices([]);
    setWorkOrderForms([]);
  };

  const filteredTasks = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return [...tasks];
    }
    return tasks.filter(
      (t) =>
        t.name?.toLowerCase().includes(term)
    );
  }, [tasks, searchTerm]);

  const getLifeToDatePercent = (task) => {
    const lifeToDate = Number(task?.lifeToDate || 0);
    const frequency = Number(task?.frequency || 0);

    if (!Number.isFinite(lifeToDate) || !Number.isFinite(frequency) || frequency === 0) {
      return 0;
    }

    return (lifeToDate / frequency) * 100;
  };


  const getProgressBarClass = (percent) => {
    if (percent >= 100) return 'bg-danger-600';
    if (percent >= 90) return 'bg-alert';
    if (percent >= 75) return 'bg-warning-600';
    return 'bg-success-600';
  };

  const sortedTasks = useMemo(() => {
    if (!sortField || !sortOrder) {
      return [...filteredTasks];
    }

    const direction = sortOrder === 'asc' ? 1 : -1;

    const getValue = (task) => {
      switch (sortField) {
        case 'name':
          return task.name?.toLowerCase() || '';
        case 'code':
          return getTaskCodeValue(task);
        case 'modifier':
          return task.modifier?.name?.toLowerCase();
        case 'type':
          return (
            task.taskType?.taskTypeCode?.toLowerCase() ||
            task.taskType?.taskTypeName?.toLowerCase() ||
            ''
          );
        case 'frequency':
          return Number(task.frequency) || 0;
        case 'occ':
          return occCounts[task.id] ?? null;
        case 'cost':
          return taskCosts[task.id] ?? null;
        case 'lcc': {
          const lcc = Number(task?.totalTaskLccCost);
          return Number.isFinite(lcc) ? lcc : null;
        }
        case 'leadTime':
          return task.taskPlanningHorizon?.name?.toLowerCase() || '';
        default:
          return '';
      }
    };

    const sorted = [...filteredTasks];
    sorted.sort((a, b) => {
      if (sortField === 'code') {
        const valueA = getTaskCodeValue(a);
        const valueB = getTaskCodeValue(b);
        const aEmpty = !valueA;
        const bEmpty = !valueB;

        if (aEmpty && bEmpty) {
          return 0;
        }

        if (aEmpty) {
          return 1;
        }

        if (bEmpty) {
          return -1;
        }

        const partsA = parseTaskCodeSortParts(valueA);
        const partsB = parseTaskCodeSortParts(valueB);

        if (partsA.isNumeric !== partsB.isNumeric) {
          return partsA.isNumeric ? -1 : 1;
        }

        if (partsA.number !== partsB.number) {
          return (partsA.number - partsB.number) * direction;
        }

        if (partsA.suffix !== partsB.suffix) {
          return partsA.suffix.localeCompare(partsB.suffix) * direction;
        }

        return valueA.toLowerCase().localeCompare(valueB.toLowerCase()) * direction;
      }

      const valueA = getValue(a);
      const valueB = getValue(b);

      if (valueA === valueB) {
        return 0;
      }

      const aEmpty = valueA === undefined || valueA === null || valueA === '';
      const bEmpty = valueB === undefined || valueB === null || valueB === '';

      if (aEmpty && bEmpty) {
        return 0;
      }

      if (aEmpty) {
        return 1;
      }

      if (bEmpty) {
        return -1;
      }

      if (typeof valueA === 'string' || typeof valueB === 'string') {
        return valueA.toString().localeCompare(valueB.toString()) * direction;
      }

      return (Number(valueA) - Number(valueB)) * direction;
    });

    return sorted;
  }, [filteredTasks, sortField, sortOrder, occCounts, taskCosts]);

  const alignScheduleTasks = useMemo(
    () => tasks.filter((task) => Number(task?.lastDonePerSchedule) !== 0),
    [tasks],
  );

  useEffect(() => {
    if (!showAlignScheduleModal) return;
    setAlignScheduleSelection(alignScheduleTasks.map((task) => task.id));
    setAlignScheduleInputs(
      alignScheduleTasks.reduce((acc, task) => {
        acc[task.id] = task?.lastDonePerSchedule ?? '';
        return acc;
      }, {}),
    );
    setAlignScheduleDirty({});
  }, [showAlignScheduleModal, alignScheduleTasks]);

  const handleAlignScheduleToggle = useCallback((taskId) => {
    setAlignScheduleSelection((prev) => (
      prev.includes(taskId)
        ? prev.filter((id) => id !== taskId)
        : [...prev, taskId]
    ));
  }, []);

  const handleAlignScheduleInputChange = useCallback((taskId, value, defaultValue) => {
    setAlignScheduleInputs((prev) => ({ ...prev, [taskId]: value }));
    const normalizedValue = value === null || value === undefined ? '' : String(value);
    const normalizedDefault =
      defaultValue === null || defaultValue === undefined ? '' : String(defaultValue);
    setAlignScheduleDirty((prev) => ({
      ...prev,
      [taskId]: normalizedValue !== normalizedDefault,
    }));
  }, []);

  const handleAlignScheduleSelectAll = useCallback(() => {
    setAlignScheduleSelection(alignScheduleTasks.map((task) => task.id));
  }, [alignScheduleTasks]);

  const handleAlignScheduleClearAll = useCallback(() => {
    setAlignScheduleSelection([]);
  }, []);

  const handleAlignScheduleSave = useCallback(async () => {
    if (alignScheduleSaving) return;
    setAlignScheduleSaving(true);

    try {
      const selectedTasks = alignScheduleTasks.filter((task) =>
        alignScheduleSelection.includes(task.id),
      );
      await Promise.all(
        selectedTasks.map((task) => {
          const inputValue = alignScheduleInputs[task.id];
          const hasCustomValue =
            alignScheduleDirty[task.id] &&
            inputValue !== null &&
            inputValue !== undefined &&
            String(inputValue).trim() !== '';
          const lastDoneValue = hasCustomValue ? Number(inputValue) : task.lastDonePerSchedule;

          return updateTasks(task.id, {
            lastDone: lastDoneValue,
            lastDoneOverride: hasCustomValue ? 0 : 1,
          });
        }),
      );

      setShowAlignScheduleModal(false);
      await loadTasksByAsset(assetId);
      showToastSuccess('Last Done values aligned to schedule.');
    } catch (err) {
      console.error('Error aligning last done to schedule:', err);
      showToastError('Unable to align Last Done values to schedule.');
    } finally {
      setAlignScheduleSaving(false);
    }
  }, [
    alignScheduleSaving,
    alignScheduleInputs,
    alignScheduleDirty,
    alignScheduleTasks,
    alignScheduleSelection,
    assetId,
    loadTasksByAsset,
    showToastError,
    showToastSuccess,
  ]);

  return (
    <div className="row g-3">
      <div className="col-8 d-flex align-items-center">
        {renderFilterControls()}
      </div>
      <div className="col-4" style={{ marginTop: "38px" }}>
        <div className="d-flex justify-content-end align-items-center flex-wrap gap-2">
          <input
            type="text"
            className="form-control form-control-sm me-2 w-auto"
            placeholder="Search tasks..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          {userProfile?.privileges?.includes('tasks:create') && (
            <>
              <div className="btn-group">
                <button
                  className="btn btn-primary btn-sm px-16 py-12"
                  onClick={() => handleOpenTaskModal()}
                >
                  <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
                  Add Task
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm dropdown-toggle dropdown-toggle-split"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <span className="visually-hidden">Toggle Dropdown</span>
                </button>
                <ul className="dropdown-menu">
                  <li className='my-6'>
                    <button
                      className="dropdown-item py-8"
                      onClick={() => {
                        loadTemplates();
                        setTemplateErrors({});
                        setShowTemplateModal(true);
                        setTemplateTask((prev) => ({
                          ...prev,
                          defaultCostCentre:
                            asset?.costCenter?.id || asset?.costCenterId || null,
                        }));
                      }}
                    >
                      <Icon
                        icon="mage:clipboard-2"
                        style={{ position: "relative", top: "-2px" }}
                        className="icon text-xl line-height-1 me-8"
                      />
                      Import from Task Template
                      <span className="visually-hidden">Toggle Dropdown</span>
                    </button>
                  </li>
                  <li className='my-6'>
                    <button
                      className="dropdown-item py-8"
                      onClick={(e) => {
                        const toggleBtn = e.currentTarget
                          .closest('.dropdown')
                          ?.querySelector('.dropdown-toggle');
                        if (toggleBtn) {
                          Dropdown.getOrCreateInstance(toggleBtn).hide();
                        }
                        openCopyModal();
                      }}
                    >
                      <Icon
                        icon="mage:delivery-truck"
                        className="icon text-xl line-height-1 me-8"
                        style={{ position: "relative", top: "-2px" }}
                      />
                      Copy from Existing Asset
                    </button>
                  </li>
                  <li className='my-6'>
                    <button
                      className="dropdown-item  py-8"
                      onClick={(e) => {
                        const toggleBtn = e.currentTarget
                          .closest('.dropdown')
                          ?.querySelector('.dropdown-toggle');
                        if (toggleBtn) {
                          Dropdown.getOrCreateInstance(toggleBtn).hide();
                        }
                        setShowTaskImporter(true);
                      }}
                    >
                      <Icon
                        icon="mage:upload"
                        className="icon text-xl line-height-1 me-8"
                        style={{ position: "relative", top: "-2px" }}
                      />
                      Import From File
                    </button>
                  </li>
                  <li className='my-6'>
                    <button
                      className="dropdown-item py-8"
                      onClick={(e) => {
                        const toggleBtn = e.currentTarget
                          .closest('.dropdown')
                          ?.querySelector('.dropdown-toggle');
                        if (toggleBtn) {
                          Dropdown.getOrCreateInstance(toggleBtn).hide();
                        }
                        setShowAlignScheduleModal(true);
                      }}
                    >
                      <Icon
                        icon="mdi:invoice-text-scheduled-outline"
                        className="icon text-xl line-height-1 me-8"
                        style={{ position: "relative", top: "-2px" }}
                      />
                      Align Last Done to Schedule
                    </button>
                  </li>
                </ul>
              </div>
            </>
          )}
        </div>
      </div>

      <div
        className="table-responsive"
        style={{ maxHeight: 'calc(100vh - 480px)', overflowY: 'auto', position: 'relative' }}
      >
        <table className="table table-sm bordered-table">
          <thead>
            <tr>
              <th style={stickyHeaderCellStyle}></th>
              {renderSortableHeader('Name', 'name')}
              {renderSortableHeader('Code', 'code')}
              {renderSortableHeader('Modifier', 'modifier')}
              {renderSortableHeader('Type', 'type')}
              {renderSortableHeader('Frequency', 'frequency')}
              <th style={stickyHeaderCellStyle}>Units</th>
              {renderSortableHeader('Occ', 'occ')}
              <th style={stickyHeaderCellStyle}>Next</th>
              <th style={stickyHeaderCellStyle}>Status</th>
              {renderSortableHeader('Task Cost', 'cost')}
              <th style={stickyHeaderCellStyle}>Cost to Date</th>
              {renderSortableHeader('LCC', 'lcc')}
              {/*{renderSortableHeader('Lead Time', 'leadTime')}*/}
              <th style={stickyHeaderCellStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!isTasksLoading && sortedTasks.length === 0 && (
              <tr>
                <td colSpan="10" className="text-center py-4 rounded-0">
                  <div className="mb-12">
                    <div className="mt-8 mb-12">No tasks loaded. Choose an import method below to get started.</div>
                    <button className="btn btn-outline-primary btn-sm"
                      onClick={() => {
                        loadTemplates();
                        setTemplateErrors({});
                        setShowTemplateModal(true);
                        setTemplateTask((prev) => ({
                          ...prev,
                          defaultCostCentre:
                            asset?.costCenter?.id || asset?.costCenterId || null,
                        }));
                      }}>
                      <Icon icon="mage:clipboard-2" className="me-2 text-xl text-primary" />
                      Import for a Template
                    </button>
                    <button className="btn btn-outline-primary btn-sm mx-12" onClick={openCopyModal}>
                      <Icon icon="mage:delivery-truck" className="me-2 text-xl text-primary me-6" />
                      Copy from Existing Asset
                    </button>
                    <button className="btn btn-outline-primary btn-sm" onClick={setShowTaskImporter}>
                      <Icon icon="mage:upload" className="me-2 text-xl text-primary" />
                      Import from a File
                    </button>
                  </div>
                </td>
              </tr>
            )}
            {isTasksLoading && (
              <tr>
                <td colSpan="12" className="text-center py-4">
                  <span className="spinner-border text-primary" role="status" aria-hidden="true" />
                </td>
              </tr>
            )}
            {[...sortedTasks, ...(editTaskId === 'new' ? [editRow] : [])].map((task) => (
              <tr
                key={task.id || task.tempId || 'new'}
                className={task.isAI ? 'bg-info-subtle' : ''}
              >
                <td>
                  <span className={`badge ${task.componentRisk === 1 ? "bg-danger" : "bg-success"}`}>
                    {task.componentRisk === 1
                      ? "R"
                      : task.componentOpportunity === 1
                        ? "O"
                        : ""}
                  </span>
                </td>
                <td className={task.isAI ? 'bg-info-subtle' : ''}>
                  {task.isAI ? <Icon icon="mage:stars-c" className="me-2 text-xl text-info-main" /> : ''}
                  {task.name}
                </td>
                <td style={{ textAlign: 'center' }}>
                  {task.code?.code}
                </td>
                <td style={{ textAlign: 'center' }}>
                  {task.modifierTask?.code}
                </td>
                <td style={{ textAlign: 'center' }}>
                  {task.taskType?.taskTypeCode || ''}
                </td>
                <td style={{ textAlign: 'end' }}>
                  {task.frequency !== 0 ? task.frequency : ''}
                  {task.cycleGroupId ?
                    <Icon
                      icon="mdi:sync"
                      className="ms-6 text-xl text-primary"
                      style={{ position: "relative", top: "-3px" }}
                    /> : ''}
                  {task.schedulingType === 'suppression' && task.schedulingGroup?.icon ? (
                    <Icon icon={task.schedulingGroup.icon} className='text-xxl text-primary ms-6' style={{ position: 'relative', top: '-2px' }} />
                  ) : ''}
                  {task.schedulingType === 'simple' ?
                    <span style={{ marginLeft: "26px" }}></span> : ''
                  }

                </td>
                <td>
                  {task.calendarBased
                    ? 'Days'
                    : task.assetMeter?.meter?.measurementUnits?.unitCode || ''}
                </td>
                <td>
                  {task?.forecastOccurrences || null}
                </td>
                <td>
                  {task.planningTask ? formatIntDate(task.nextOccurrenceDate || null) : ''}
                </td>
                <td>
                  {(() => {
                    const lifeToDatePercent = getLifeToDatePercent(task);
                    const boundedPercent = Math.max(0, Math.min(lifeToDatePercent, 100));
                    const progressBarClass = getProgressBarClass(lifeToDatePercent);

                    return (
                      <div
                        className={`progress h-8-px w-100`}
                        role="progressbar"
                        aria-label="Life to date progress"
                        aria-valuenow={Math.max(0, lifeToDatePercent)}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      >
                        <div
                          className={`progress-bar rounded-pill ${progressBarClass}`}
                          style={{ width: `${boundedPercent}%` }}
                        />
                      </div>
                    );
                  })()}
                </td>
                <td style={{ textAlign: 'end' }}>
                  {taskCosts[task.id] !== undefined ? `${Number(taskCosts[task.id]?.toFixed(0) || '0').toLocaleString()}` : ''}
                </td>
                <td style={{ textAlign: 'end' }}>
                  {Number(task?.costToDate?.toFixed(0) || '0').toLocaleString()}
                </td>
                <td style={{ textAlign: 'end' }}>
                  {Number(task?.totalTaskLccCost?.toFixed(0) || '0').toLocaleString()}
                </td>
                {/*<td>
                  {task.planningTask ? task.taskPlanningHorizon?.name || '' : ''}
                </td>*/}
                <td>
                  {editTaskId === task.id || (editTaskId === 'new' && !task.id) ? (
                    <>
                      <button className="edit-btn w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center mx-4" onClick={handleInlineSave}>
                        <Icon icon="ic:baseline-check" />
                      </button>
                      <button
                        className="edit-btn w-32-px h-32-px bg-secondary-focus text-secondary-main rounded-circle d-inline-flex align-items-center justify-content-center"
                        onClick={() => {
                          setEditTaskId(task.id);
                          setEditRow({
                            ...task,
                            codeId: task.codeId,
                            modifierCodeId: task.modifierCodeId,
                            taskTypeId: task.taskType?.id || '',
                            assetMeterId: task.assetMeterId?.id || '',
                          });
                        }}
                      >
                        <Icon icon="ic:baseline-refresh" />
                      </button>
                    </>
                  ) : (
                    <div className="d-flex">
                      <>
                        <button
                          className="edit-btn w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center mx-4"
                          onClick={() => handleOpenTaskModal(task)}
                        >
                          <Icon icon="ic:baseline-edit" />
                        </button>
                        <button
                          className="delete-btn w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={async () => {
                            if (window.confirm('Delete this task?')) {
                              try {
                                await deleteTasks(task.id);
                                await loadTasksByAsset(assetId);
                              } catch (error) {
                                const message =
                                  error?.response?.data?.message ||
                                  error?.message ||
                                  'Failed to delete task. Please try again later.';

                                showToastError(message);
                              }
                            }
                          }}
                        >
                          <Icon icon="ic:baseline-delete-outline" />
                        </button>
                      </>
                      {Boolean(task.planningTask) && (
                        <button
                          className="w-32-px h-32-px ms-4 bg-secondary-focus text-secondary-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleShowOccurrences(task)}
                        >
                          <Icon icon="mdi:calendar-month-outline" />
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {showModal && (
          <>
            <div className="modal modal-backdrop show d-block" tabIndex="-1">
              <div className="modal-dialog modal-xl">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      {isNewModalTask ? 'Add Task' : 'Edit Task'}
                    </h5>
                    <button
                      className="btn-close"
                      onClick={() => {
                        resetCycleState();
                        setShowModal(false);
                        setIsLastDoneLocked(true);
                        setShowLastDoneWarning(false);
                        setLatestMeterReading(null);
                        setPartsOverride(false);
                        setLabourOverride(false);
                        setServicesOverride(false);
                        setShowTaskFormModal(false);
                        setShowTaskFormPreviewModal(false);
                        setPreviewTaskForm(null);
                        initialRotablesIdRef.current = null;
                      }}
                    />
                  </div>
                  <div className="modal-body">
                    <ul className="nav nav-tabs mb-3" id="taskModalTabs" role="tablist">
                      <li className="nav-item" role="presentation">
                        <button className="nav-link active" id="task-general-tab" data-bs-toggle="tab" data-bs-target="#task-general" type="button" role="tab">
                          General{renderErrorDot(generalTabHasError)}
                        </button>
                      </li>
                      <li className="nav-item" role="presentation">
                        <button className="nav-link" id="task-scheduling-tab" data-bs-toggle="tab" data-bs-target="#task-scheduling" type="button" role="tab">
                          Scheduling{renderErrorDot(schedulingTabHasError)}
                        </button>
                      </li>
                      <li className="nav-item" role="presentation">
                        <button className="nav-link" id="task-parts-tab" data-bs-toggle="tab" data-bs-target="#task-parts" type="button" role="tab">
                          Parts{renderErrorDot(partsTabHasError)}
                        </button>
                      </li>
                      <li className="nav-item" role="presentation">
                        <button className="nav-link" id="task-labour-tab" data-bs-toggle="tab" data-bs-target="#task-labour" type="button" role="tab">
                          Labour{renderErrorDot(labourTabHasError)}
                        </button>
                      </li>
                      <li className="nav-item" role="presentation">
                        <button className="nav-link" id="task-services-tab" data-bs-toggle="tab" data-bs-target="#task-services" type="button" role="tab">
                          Services{renderErrorDot(serviceTabHasError)}
                        </button>
                      </li>
                      {useRotables && (
                        <li className="nav-item" role="presentation">
                          <button className="nav-link" id="task-rotables-tab" data-bs-toggle="tab" data-bs-target="#task-rotables" type="button" role="tab">
                            Rotables
                          </button>
                        </li>
                      )}
                      <li className="nav-item" role="presentation">
                        <button className="nav-link" id="task-equipment-tab" data-bs-toggle="tab" data-bs-target="#task-equipment" type="button" role="tab">Tools & Resources</button>
                      </li>
                      <li className="nav-item" role="presentation">
                        <button className="nav-link" id="task-forms-tab" data-bs-toggle="tab" data-bs-target="#task-forms" type="button" role="tab" onClick={handleTaskFormsTabEnter}>Forms</button>
                      </li>
                      <li className="nav-item" role="presentation">
                        <button className="nav-link" id="task-documents-tab" data-bs-toggle="tab" data-bs-target="#task-documents" type="button" role="tab">Documents</button>
                      </li>
                    </ul>
                    <div className="tab-content pt-3">
                      <div className="tab-pane fade show active" id="task-general" role="tabpanel">
                        <div className="row g-3">
                          {dynamicTaskFormFields
                            .filter((field) => field.field !== 'lastDone')
                            .map(renderTaskField)}
                          <div className="col-md-6"></div>
                          <div className="col-md-4">
                            <label className="form-label">Parts Cost</label>
                            <input
                              type="number"
                              step="0.01"
                              className="form-control"
                              value={modalTask?.partsPrice || 0}
                              disabled
                            />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label">Labour Cost</label>
                            <input
                              type="number"
                              step="0.01"
                              className="form-control"
                              value={modalTask?.labourCost || 0}
                              disabled
                            />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label">Other Cost</label>
                            <input
                              type="number"
                              step="0.01"
                              className="form-control"
                              value={modalTask?.miscPrice || 0}
                              disabled
                            />
                          </div>
                        </div>
                      </div>
                      <div className="tab-pane fade" id="task-scheduling" role="tabpanel">
                        <div className="row g-3">
                          {schedulingFormFields
                            .filter((field) => field.field === 'schedulingType')
                            .map(renderSchedulingField)}

                          {[forecastBasisField].map(renderSchedulingField)}
                          <div className="col-md-2">
                            <label className="form-label">
                              {lastDoneLabel}
                            </label>
                            <div className="d-flex align-items-center gap-2">
                              <input
                                type={Number(modalTask?.calendarBased) === 1 ? 'date' : 'number'}
                                inputMode={Number(modalTask?.calendarBased) === 1 ? undefined : 'numeric'}
                                step={Number(modalTask?.calendarBased) === 1 ? undefined : '1'}
                                min={Number(modalTask?.calendarBased) === 1 ? undefined : '0'}
                                className={`form-control ${modalErrors.lastDone ? 'is-invalid' : ''}`}
                                value={modalTask?.lastDone ?? ''}
                                onChange={handleLastDoneChange}
                                disabled={isLastDoneLocked}
                                autoComplete="off"
                                style={{ width: "300px" }}
                              />
                              {Number(modalTask?.lastDoneOverride) === 1 && (
                                <span className="badge bg-primary-focus text-primary">Est.</span>
                              )}
                            </div>
                            {modalErrors.lastDone && (
                              <div className="invalid-feedback">{modalErrors.lastDone}</div>
                            )}
                          </div>
                          <div className="col-md-2 d-flex mb-8 align-items-end">
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm ms-2 d-flex align-items-center gap-1 ms-48"
                              onClick={handleLastDoneToggle}
                              title={isLastDoneLocked ? 'Unlock last known change' : 'Lock last known change'}
                            >
                              <Icon icon="mdi:lock-open-outline" className="icon text-xl line-height-1" />
                              {isLastDoneLocked ? 'Unlock' : 'Lock'}
                            </button>
                          </div>
                          <div className="col-md-2"></div>
                        </div>
                        <div className="row g-3">
                          {schedulingFormFields
                            .filter((field) => field.field !== 'schedulingType')
                            .map(renderSchedulingField)}
                        </div>
                        {modalTask?.schedulingType === 'cycle' && (
                          <div className="mt-4">
                            <div className="row g-3 mt-16">{cycleGroupFields.map(renderCycleGroupField)}</div>
                            {isCycleLoading ? (
                              <div className="d-flex align-items-center gap-2 mt-3">
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                <span>Loading cycle tasks…</span>
                              </div>
                            ) : (
                              <CycleTasksEditor
                                calendarBased={modalTask?.calendarBased}
                                cycleRows={cycleRows}
                                cycleRowErrors={cycleRowErrors}
                                cycleTaskOptions={cycleTaskOptions}
                                baseTaskLabel={baseTaskLabel}
                                baseMeterId={baseMeterId}
                                isCycleLoading={isCycleLoading}
                                onDragEnd={handleCycleDragEnd}
                                onRowChange={handleCycleRowChange}
                                onDeleteRow={handleDeleteCycleRow}
                                onAddRow={handleAddCycleRow}
                              />
                            )}
                          </div>
                        )}
                      </div>
                      <div className="tab-pane fade" id="task-parts" role="tabpanel">
                        <div className="d-flex justify-content-end">
                          <div className="btn-group">
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => {
                                setPartForm({
                                  partsId: null,
                                  optional: 0,
                                  parts: null,
                                  partNumber: '',
                                  partDescription: '',
                                  quantity: 1,
                                  partsPrice: '',
                                  pricingDate: toDateInputValue(new Date().toISOString()),
                                  defaultExpenseCode: defaultExpenseParts,
                                });
                                setSelectedPartOption(null);
                                setPartModalErrors({});
                                setEditingPartIndex(null);
                                setShowPartModal(true);
                              }}
                            >
                              + Add Part
                            </button>
                            <button
                              type="button"
                              className="btn btn-primary btn-sm dropdown-toggle dropdown-toggle-split"
                              data-bs-toggle="dropdown"
                              aria-expanded="false"
                            >
                              <span className="visually-hidden">Toggle Dropdown</span>
                            </button>
                            <ul className="dropdown-menu dropdown-menu-end">
                              <li className="my-6">
                                <button
                                  className="dropdown-item py-8 d-flex align-items-center gap-2"
                                  onClick={(e) => {
                                    const toggleBtn = e.currentTarget
                                      .closest('.btn-group')
                                      ?.querySelector('.dropdown-toggle');
                                    if (toggleBtn) {
                                      Dropdown.getOrCreateInstance(toggleBtn).hide();
                                    }
                                    openBomAiModal();
                                  }}
                                >
                                  <Icon icon="mdi:stars-outline" className="icon text-xl line-height-1" />
                                  Import with AI
                                </button>
                              </li>
                            </ul>
                          </div>
                        </div>
                        <div className="row g-3">{partsTabFields.map(renderPartsTabField)}</div>
                        <div className="table-responsive mt-3"
                        style={{ maxHeight: 'calc(100vh - 400px)'}}>
                          <table className="table bordered-table">
                            <thead>
                              <tr>
                                <th>Part #</th>
                                <th>Description</th>
                                <th>Parts Cost</th>
                                <th>Pricing Date</th>
                                <th>Qty</th>
                                <th>Optional</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {taskMaterials.length === 0 && (
                                <tr>
                                  <td colSpan="6" className="text-center py-4 rounded-0">No Parts Added</td>
                                </tr>
                              )}
                              {taskMaterials.map((p, idx) => (
                                <tr key={idx}>
                                  <td>{p.partNumber || p.parts?.partNumber}</td>
                                  <td>{p.partDescription || p.parts?.description}</td>
                                  <td>{p.partsPrice}</td>
                                  <td>{p.pricingDate ? new Date(p.pricingDate).toLocaleDateString() : ''}</td>
                                  <td>{p.quantity}</td>
                                  <td>
                                    <div className="form-switch switch-primary d-flex align-items-center gap-3 mb-0">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        id={`optional-switch-${p.id}`}
                                        checked={p.optional === 1}
                                        disabled
                                      />
                                      <label className="form-check-label" htmlFor={`optional-switch-${p.id}`}>
                                        {p.optional === 1 ? 'Yes' : 'No'}
                                      </label>
                                    </div>
                                  </td>
                                  <td>
                                    <>
                                      <button
                                        className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                                        onClick={() => {
                                          setEditingPartIndex(idx);
                                          const partNumber = p.partNumber ?? p.parts?.partNumber ?? '';
                                          const partDescription = p.partDescription ?? p.parts?.description ?? '';
                                          const partId = p.partsId ?? p.parts?.id ?? null;
                                          const normalizedPart = p.parts
                                            ? { ...p.parts }
                                            : partId
                                              ? {
                                                id: partId,
                                                partNumber,
                                                description: partDescription,
                                                pricingDate: p.pricingDate,
                                                lastPrice: p.lastPrice ?? p.partsPrice,
                                              }
                                              : null;
                                          setPartForm({
                                            ...p,
                                            partsId: partId,
                                            parts: normalizedPart,
                                            partNumber,
                                            partDescription,
                                          });
                                          setSelectedPartOption(
                                            partId
                                              ? {
                                                value: partId,
                                                label:
                                                  formatPartOptionLabel(partNumber, partDescription) ||
                                                  `Part ${partId}`,
                                                raw: normalizedPart || {
                                                  id: partId,
                                                  partNumber,
                                                  description: partDescription,
                                                },
                                              }
                                              : null,
                                          );
                                          setPartModalErrors({});
                                          setShowPartModal(true);
                                        }}
                                      >
                                        <Icon icon="ic:baseline-edit" />
                                      </button>
                                      <button className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center" onClick={() => { if (window.confirm('Delete?')) { setTaskMaterials(prev => prev.filter((_, i) => i !== idx)); if (p.id) setDeletedMaterialIds(prev => [...prev, p.id]); } }}><Icon icon="ic:baseline-delete-outline" /></button>
                                    </>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div className="tab-pane fade" id="task-equipment" role="tabpanel">
                        <div className="d-flex justify-content-end">
                          <button className="btn btn-primary btn-sm" onClick={() => { setEquipmentForm({ name: '', quantity: 1, notes: '' }); setEditingEquipmentIndex(null); setShowEquipmentModal(true); }}>+ Add Resources</button>
                        </div>
                        <div className="table-responsive mt-3"
                        style={{ maxHeight: 'calc(100vh - 400px)'}}>
                          <table className="table bordered-table">
                            <thead><tr><th>Name</th><th>Qty</th><th>Actions</th></tr></thead>
                            <tbody>
                              {taskEquipment.length === 0 && (
                                <tr>
                                  <td colSpan="4" className="text-center py-4 rounded-0">No Tools or Resources Added</td>
                                </tr>
                              )}
                              {taskEquipment.map((eq, idx) => (
                                <tr key={idx}>
                                  <td>{eq.name}</td>
                                  <td>{eq.quantity}</td>
                                  <td>
                                    <>
                                      <button className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center me-2" onClick={() => { setEditingEquipmentIndex(idx); setEquipmentForm(eq); setShowEquipmentModal(true); }}><Icon icon="ic:baseline-edit" /></button>
                                      <button className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center" onClick={() => { if (window.confirm('Delete?')) { setTaskEquipment(prev => prev.filter((_, i) => i !== idx)); if (eq.id) setDeletedEquipmentIds(prev => [...prev, eq.id]); } }}><Icon icon="ic:baseline-delete-outline" /></button>
                                    </>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div className="tab-pane fade" id="task-labour" role="tabpanel">
                        <div className="d-flex justify-content-end">
                          <button className="btn btn-primary btn-sm" onClick={() => { setLabourForm({ tradeId: '', qty: 1, labourHours: '', defaultExpenseCode: defaultExpenseLabour, notes: '', labourRate: 0 }); setEditingLabourIndex(null); setShowLabourModal(true); }}>+ Add Labour</button>
                        </div>
                        <div className="row g-3">{labourTabFields.map(renderLabourTabField)}</div>
                        <div className="table-responsive mt-3"
                        style={{ maxHeight: 'calc(100vh - 400px)'}}>
                          <table className="table bordered-table">
                            <thead><tr><th>Trade</th><th>Qty</th><th>Hours</th><th>Expense</th><th>Hourly Rate</th><th>Actions</th></tr></thead>
                            <tbody>
                              {taskLabour.length === 0 && (
                                <tr>
                                  <td colSpan="6" className="text-center py-4 rounded-0">No Labour Added</td>
                                </tr>
                              )}
                              {taskLabour.map((lb, idx) => (
                                <tr key={idx}>
                                  <td>{tradeOptions.find(t => t.id === lb.tradeId)?.name || lb.tradeId}</td>
                                  <td>{lb.qty}</td>
                                  <td>{lb.labourHours}</td>
                                  <td>{expenseOptions.find(e => e.id === lb.defaultExpenseCode)?.name || lb.defaultExpenseCode}</td>
                                  <td>{lb.labourRate}</td>
                                  <td>
                                    <>
                                      <button className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center me-2" onClick={() => { setEditingLabourIndex(idx); setLabourForm(lb); setShowLabourModal(true); }}><Icon icon="ic:baseline-edit" /></button>
                                      <button className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center" onClick={() => { if (window.confirm('Delete?')) { setTaskLabour(prev => prev.filter((_, i) => i !== idx)); if (lb.id) setDeletedLabourIds(prev => [...prev, lb.id]); } }}><Icon icon="ic:baseline-delete-outline" /></button>
                                    </>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div className="tab-pane fade" id="task-services" role="tabpanel">
                        <div className="d-flex justify-content-end">
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => {
                              setServiceForm({
                                serviceId: '',
                                quantity: 1,
                                defaultExpenseCode: defaultExpenseServices,
                                notes: '',
                                cost: '',
                              });
                              setEditingServiceIndex(null);
                              setShowServiceModal(true);
                            }}
                          >
                            + Add Service
                          </button>
                        </div>
                        <div className="row g-3">{serviceTabFields.map(renderServiceTabField)}</div>
                        <div className="table-responsive mt-3"
                        style={{ maxHeight: 'calc(100vh - 400px)'}}>
                          <table className="table bordered-table">
                            <thead><tr><th>Service</th><th>Qty</th><th>Expense</th><th>Cost</th><th>Actions</th></tr></thead>
                            <tbody>
                              {taskServices.length === 0 && (
                                <tr>
                                  <td colSpan="5" className="text-center py-4 rounded-0">No Services Added</td>
                                </tr>
                              )}
                              {taskServices.map((sv, idx) => (
                                <tr key={idx}>
                                  <td>{serviceOptions.find(s => s.id === sv.serviceId)?.name || sv.serviceId}</td>
                                  <td>{sv.quantity}</td>
                                  <td>{expenseOptions.find(e => e.id === sv.defaultExpenseCode)?.name || sv.defaultExpenseCode}</td>
                                  <td>{sv.cost}</td>
                                  <td>
                                    <>
                                      <button className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center me-2" onClick={() => { setEditingServiceIndex(idx); setServiceForm(sv); setShowServiceModal(true); }}><Icon icon="ic:baseline-edit" /></button>
                                      <button className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center" onClick={() => { if (window.confirm('Delete?')) { setTaskServices(prev => prev.filter((_, i) => i !== idx)); if (sv.id) setDeletedServiceIds(prev => [...prev, sv.id]); } }}><Icon icon="ic:baseline-delete-outline" /></button>
                                    </>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      {useRotables && (
                        <div className="tab-pane fade" id="task-rotables" role="tabpanel">
                          <div className="row g-3">{rotableTabFields.map(renderRotableTabField)}</div>
                          {Array.isArray(modalTask.rotableTransactionFiles) &&
                            modalTask.rotableTransactionFiles.length > 0 && (
                              <DocumentDataTable
                                files={modalTask.rotableTransactionFiles}
                                onDelete={handleRotableTransactionFileDelete}
                                directory="partsfiles"
                              />
                            )}
                          {selectedRotable && (
                            <div className="row g-3 mt-2">
                              <div className="col-md-6">
                                <label className="form-label">Serial Number</label>
                                <input
                                  className="form-control"
                                  value={selectedRotable.serialNumber ?? ''}
                                  readOnly
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Arrangement Number</label>
                                <input
                                  className="form-control"
                                  value={selectedRotable.arrangementNumber ?? ''}
                                  readOnly
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Part Number</label>
                                <input
                                  className="form-control"
                                  value={
                                    selectedRotable.part?.partNumber ??
                                    selectedRotable.partNumber ??
                                    ''
                                  }
                                  readOnly
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Part Description</label>
                                <input
                                  className="form-control"
                                  value={selectedRotable.part?.description ?? ''}
                                  readOnly
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Stock Material Number</label>
                                <input
                                  className="form-control"
                                  value={selectedRotable.stockItem?.materialNumber ?? ''}
                                  readOnly
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Component Type</label>
                                <input
                                  className="form-control"
                                  value={selectedRotable.componentType?.name ?? ''}
                                  readOnly
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Supplier</label>
                                <input
                                  className="form-control"
                                  value={selectedRotable.supplier?.name ?? ''}
                                  readOnly
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Warranty Start</label>
                                <input
                                  className="form-control"
                                  value={
                                    selectedRotable.warrantyStart
                                      ? formatIntDate(selectedRotable.warrantyStart)
                                      : ''
                                  }
                                  readOnly
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Warranty End</label>
                                <input
                                  className="form-control"
                                  value={
                                    selectedRotable.warrantyEnd
                                      ? formatIntDate(selectedRotable.warrantyEnd)
                                      : ''
                                  }
                                  readOnly
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Number of Turns</label>
                                <input
                                  className="form-control"
                                  value={selectedRotable.numberOfTurns ?? ''}
                                  readOnly
                                />
                              </div>
                              <div className="col-12">
                                <label className="form-label">Notes</label>
                                <textarea
                                  className="form-control"
                                  rows="3"
                                  value={selectedRotable.notes ?? ''}
                                  readOnly
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="tab-pane fade" id="task-forms" role="tabpanel">
                        <div className="d-flex justify-content-end">
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={openAddTaskFormModal}
                          >
                            + Add Form
                          </button>
                        </div>
                        <div className="table-responsive mt-3"
                        style={{ maxHeight: 'calc(100vh - 400px)'}}>
                          <table className="table bordered-table">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedTaskForms.length === 0 && (
                                <tr>
                                  <td colSpan="10" className="text-center py-4 rounded-0">No Forms attached to Task</td>
                                </tr>
                              )}
                              {selectedTaskForms.map((form) => (
                                <tr key={form.id}>
                                  <td>{form.name}</td>
                                  <td>
                                    <button
                                      className="view-btn w-32-px h-32-px bg-info-focus text-info-main rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                                      onClick={() => handleViewTaskForm(form.id)}
                                    >
                                      <Icon icon="ic:baseline-visibility" />
                                    </button>
                                    <button
                                      className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                      onClick={() => handleRemoveTaskForm(form.id)}
                                    >
                                      <Icon icon="ic:baseline-delete-outline" />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div className="tab-pane fade" id="task-documents" role="tabpanel">
                        <div className="row g-3">{taskDocumentFields.map(renderDocumentFields)}</div>
                        {Array.isArray(modalTask.taskFiles) && modalTask.taskFiles.length > 0 && (
                          <DocumentDataTable files={modalTask.taskFiles} onDelete={handleFileDelete} directory="taskfiles" />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        resetCycleState();
                        setShowModal(false);
                        setIsLastDoneLocked(true);
                        setShowLastDoneWarning(false);
                        setLatestMeterReading(null);
                        setPartsOverride(false);
                        setLabourOverride(false);
                        setServicesOverride(false);
                        lastDoneEditedRef.current = false;
                        initialLastDoneRef.current = '';
                        initialRotablesIdRef.current = null;
                      }}
                    >
                      Cancel
                    </button>
                    <button className="btn btn-primary" onClick={handleModalSave}>
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {showLastDoneWarning && (
              <>
                <div className="modal-backdrop fade show" style={{ zIndex: 1060 }}></div>
                <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1061 }}>
                  <div className="modal-dialog" style={{ top: '20vh' }}>
                    <div className="modal-content">
                      <div className="modal-body">
                        <h6 className="fw-semibold">
                          <Icon icon="mdi:block" className="icon text-danger" style={{ position: "relative", top: "-3px" }} />
                          <span className="ms-8">Warning</span></h6>
                        <p className="mb-0">
                          The Last Known Reading records the last component change before the asset was added to Samurai.
                          If the component is being changed now, use a work order instead. Either create one directly or use Change Component option on the Component Management page.
                        </p>
                      </div>
                      <div className="modal-footer">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => {
                            setIsLastDoneLocked(true);
                            setShowLastDoneWarning(false);
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => setShowLastDoneWarning(false)}
                        >
                          Ok, I Understand
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
            {showSchedulingTypeConfirm && (
              <>
                <div className="modal-backdrop fade show" style={{ zIndex: 1060 }}></div>
                <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1061 }}>
                  <div className="modal-dialog" style={{ top: '20vh' }}>
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">Change Scheduling Type</h5>
                      </div>
                      <div className="modal-body">
                        Switching the Scheduling Type will remove all tasks from this cycle group. Do you want to continue?
                      </div>
                      <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={handleSchedulingTypeCancel}>
                          Cancel
                        </button>
                        <button type="button" className="btn btn-primary" onClick={handleSchedulingTypeConfirm}>
                          OK
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
        {showBomAiModal && (
          <div className="modal modal-backdrop show d-block" tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <Icon
                      className="text-xxl me-12 text-primary"
                      style={{ position: 'relative', top: '-4px' }}
                      icon="mdi:stars-outline"
                    />
                    Add Bill of Materials
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeBomAiModal}
                    disabled={bomAiLoading}
                  />
                </div>
                <div className="modal-body">
                  <div className="text-sm text-secondary">
                    <p>
                      Copy and Paste a bill of materials list in any format into the text area below. Make sure the
                      text contains bill of materials items for the selected tasks with Part Number, Description,
                      Quantity as a minimum. Optionally include the Price, Pricing Date and Optional flag for each
                      part.
                    </p>
                  </div>
                  {bomAiError && (
                    <div className="alert alert-danger" role="alert">
                      {bomAiError}
                    </div>
                  )}
                  <textarea
                    className="form-control"
                    rows={6}
                    value={bomAiText}
                    onChange={(e) => setBomAiText(e.target.value)}
                    disabled={bomAiLoading}
                    placeholder="Paste a list of parts in any format"
                  />
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={closeBomAiModal} disabled={bomAiLoading}>
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleImportBomWithAi}
                    disabled={bomAiLoading || !bomAiText.trim()}
                  >
                    {bomAiLoading ? 'Adding...' : 'Add with AI'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {showMissingBomModal && (
          <div className="modal modal-backdrop show d-block" tabIndex="-1">
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Add New Parts to Catalogue</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      if (missingBomSaving) return;
                      setShowMissingBomModal(false);
                      setMissingBomParts([]);
                      setMissingBomError('');
                    }}
                    disabled={missingBomSaving}
                  />
                </div>
                <div className="modal-body">
                  {missingBomError && (
                    <div className="alert alert-danger" role="alert">
                      {missingBomError}
                    </div>
                  )}
                  <div className="row g-3 mb-3">
                    <div className="col-md-6">
                      <label className="form-label">Source of Supply</label>
                      <select
                        className="form-select"
                        value={bomGlobalSourceId}
                        onChange={(e) => handleGlobalBomSourceChange(e.target.value)}
                      >
                        <option value="">Select a source of supply</option>
                        {bomManufacturerOptions.map((manufacturer) => (
                          <option key={manufacturer.id} value={manufacturer.id}>
                            {manufacturer.manufacturer}
                          </option>
                        ))}
                      </select>
                      <div className="form-check mt-16">
                        <input
                          className="form-check-input me-8"
                          style={{ position: "realtive", top: "2px" }}
                          type="checkbox"
                          id="bom-source-apply-all"
                          checked={bomApplySourceToAll}
                          onChange={(e) => handleApplySourceToAllChange(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="bom-source-apply-all">
                          Apply to all parts
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Preferred Supplier</label>
                      <select
                        className="form-select"
                        value={bomGlobalSupplierId}
                        onChange={(e) => handleGlobalBomSupplierChange(e.target.value)}
                      >
                        <option value="">Select a supplier</option>
                        {bomSupplierOptions.map((supplier) => (
                          <option key={supplier.id} value={supplier.id}>
                            {supplier.name}
                          </option>
                        ))}
                      </select>
                      <div className="form-check mt-16">
                        <input
                          className="form-check-input me-8"
                          style={{ position: "realtive", top: "2px" }}
                          type="checkbox"
                          id="bom-supplier-apply-all"
                          checked={bomApplySupplierToAll}
                          onChange={(e) => handleApplySupplierToAllChange(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="bom-supplier-apply-all">
                          Apply to all parts
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="table-responsive"
                  style={{ maxHeight: 'calc(100vh - 400px)'}}>
                    <table className="table bordered-table">
                      <thead>
                        <tr>
                          <th>Part #</th>
                          <th>Description</th>
                          <th>Qty</th>
                          <th>Parts Cost</th>
                          <th>Pricing Date</th>
                          <th>Optional</th>
                          <th>Source of Supply</th>
                          <th>Preferred Supplier</th>
                        </tr>
                      </thead>
                      <tbody>
                        {missingBomParts.map((part, index) => (
                          <tr key={`${part.partNumber}-${index}`}>
                            <td>{part.partNumber}</td>
                            <td>{part.partDescription}</td>
                            <td>{part.quantity}</td>
                            <td>{part.partsPrice}</td>
                            <td>{part.pricingDate ? new Date(part.pricingDate).toLocaleDateString() : ''}</td>
                            <td>{part.optional === 1 ? 'Yes' : 'No'}</td>
                            <td>
                              <select
                                className="form-select form-select-sm"
                                value={part.sourceOfSupplyId}
                                onChange={(e) =>
                                  handleMissingBomUpdate(index, { sourceOfSupplyId: e.target.value })
                                }
                                disabled={bomApplySourceToAll}
                              >
                                <option value="">Select</option>
                                {bomManufacturerOptions.map((manufacturer) => (
                                  <option key={manufacturer.id} value={manufacturer.id}>
                                    {manufacturer.manufacturer}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <select
                                className="form-select form-select-sm"
                                value={part.supplierId}
                                onChange={(e) =>
                                  handleMissingBomUpdate(index, { supplierId: e.target.value })
                                }
                                disabled={bomApplySupplierToAll}
                              >
                                <option value="">Select</option>
                                {bomSupplierOptions.map((supplier) => (
                                  <option key={supplier.id} value={supplier.id}>
                                    {supplier.name}
                                  </option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      if (missingBomSaving) return;
                      setShowMissingBomModal(false);
                      setMissingBomParts([]);
                      setMissingBomError('');
                    }}
                    disabled={missingBomSaving}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleSaveMissingBomParts}
                    disabled={missingBomSaving || missingBomParts.length === 0}
                  >
                    {missingBomSaving ? 'Saving...' : 'Add Parts'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        <AddPartsModal
          show={showPartModal}
          title={editingPartIndex !== null ? 'Edit Part' : 'Add Part to Task'}
          fields={partModalFields}
          renderField={renderPartModalField}
          onCancel={handlePartModalClose}
          onSave={handlePartModalSave}
        />
        {showTaskFormModal && (
          <div className="modal modal-backdrop show d-block" tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Add Form</h5>
                  <button type="button" className="btn-close" onClick={() => setShowTaskFormModal(false)} />
                </div>
                <div className="modal-body">
                  <div className="mb-2">
                    <label className="form-label">Select Form</label>
                    <select
                      className="form-select"
                      value={selectedTaskFormId}
                      onChange={(e) => setSelectedTaskFormId(e.target.value)}
                    >
                      <option value="">Select…</option>
                      {taskFormOptions
                        .filter((form) => !selectedTaskForms.some((selected) => Number(selected.id) === Number(form.id)))
                        .map((form) => (
                          <option key={form.id} value={form.id}>{form.name}</option>
                        ))}
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowTaskFormModal(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleSaveTaskFormSelection}>Save</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showTaskFormPreviewModal && (
          <div className="modal modal-backdrop show d-block" tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content" style={{ width: "655px" }}>
                <div className="modal-header h-0 p-0" style={{ marginTop: "-12px"}}>
                  <button
                    type="button"
                    className="btn-close"
                    style= {{
                      color: "#fff !important",
                      zIndex: 1000,
                      position: "relative",
                      top: "36px",
                      right: "16px"
                    }}
                  onClick={() => {
                    setShowTaskFormPreviewModal(false);
                    setPreviewTaskForm(null);
                  }}
                  />
                </div>
                <div className="modal-body p-0" style={{ overflowY: 'auto' }}>
                  {taskFormPreviewLoading ? (
                    <div className="py-4 text-center">Loading preview...</div>
                  ) : (
                    <FormLivePreview
                      form={previewTaskForm}
                      items={previewTaskForm?.tblFormItems || []}
                      renderPreviewInput={renderTaskFormPreviewInput}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {showEquipmentModal && (
          <div className="modal modal-backdrop show d-block" tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{editingEquipmentIndex !== null ? 'Edit' : 'Add'} Tools & Resources</h5>
                  <button type="button" className="btn-close" onClick={() => setShowEquipmentModal(false)} />
                </div>
                <div className="modal-body">
                  <div className="row g-3">{equipmentModalFields.map(renderEquipmentModalField)}</div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowEquipmentModal(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleEquipmentModalSave}>Save</button>
                </div>
              </div>
            </div>
          </div>
        )}
        {showLabourModal && (
          <div className="modal modal-backdrop show d-block" tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{editingLabourIndex !== null ? 'Edit' : 'Add'} Labour</h5>
                  <button type="button" className="btn-close" onClick={() => setShowLabourModal(false)} />
                </div>
                <div className="modal-body">
                  <div className="row g-3">{labourModalFields.map(renderLabourModalField)}</div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowLabourModal(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleLabourModalSave}>Save</button>
                </div>
              </div>
            </div>
          </div>
        )}
        {showServiceModal && (
          <div className="modal modal-backdrop show d-block" tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{editingServiceIndex !== null ? 'Edit' : 'Add'} Service</h5>
                  <button type="button" className="btn-close" onClick={() => setShowServiceModal(false)} />
                </div>
                <div className="modal-body">
                  <div className="row g-3">{serviceModalFields.map(renderServiceModalField)}</div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowServiceModal(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleServiceModalSave}>Save</button>
                </div>
              </div>
            </div>
          </div>
        )}
        <AddPartModal
          show={showCreatePartModal}
          onClose={() => setShowCreatePartModal(false)}
          onPartCreated={handlePartCreatedFromModal}
          initialValues={addPartInitialValues}
          selectedExpenseCodeId={defaultExpenseParts}
          expenseOptions={expenseOptions}
        />
        <TaskOccurrencesModal
          show={showOccurrencesModal}
          taskOccurrences={taskOccurrences}
          workOrderStatuses={workOrderStatuses}
          formatDate={formatIntDate}
          onWorkOrderClick={handleWorkOrderLinkClick}
          onClose={() => {
            setShowOccurrencesModal(false);
          }}
        />
        {showWorkOrderDetailsModal && (
          <div className="modal modal-backdrop show d-block" tabIndex="-1">
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {workOrderDetailsLoading ? ("Work Order Details") : (`${workOrderDetails.workOrderNumber} - ${workOrderDetails.workOrderTitle}`)}
                  </h5>
                  <button type="button" className="btn-close" onClick={handleCloseWorkOrderDetails} />
                </div>
                <div className="modal-body">
                  {workOrderDetailsLoading ? (
                    <div className="d-flex justify-content-center align-items-center py-4">
                      <div className="spinner-border" role="status" aria-label="Loading work order details" />
                    </div>
                  ) : (
                    <WorkOrderDetails
                      workOrder={workOrderDetails}
                      parts={workOrderParts}
                      labour={workOrderLabour}
                      services={workOrderServices}
                      equipment={workOrderEquipment}
                      forms={workOrderForms}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        {showTemplateModal && (
          <div className="modal modal-backdrop show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Select Task Template</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowTemplateModal(false)}
                  />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Select Template</label>
                    <Select
                      classNamePrefix="react-select"
                      value={selectedTemplate}
                      onChange={handleTemplateSelect}
                      options={templateOptions}
                      placeholder="Select Template"
                    />
                  </div>
                  <div className="row g-3">
                    {templateFormFields.map(renderTemplateField)}
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowTemplateModal(false)}>
                    Cancel
                  </button>
                  <button className="btn btn-primary" onClick={handleTemplateSave}>
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>

        )}
        {showCopyFromAssetModal && (
          <div className="modal modal-backdrop show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Copy Tasks from Asset</h5>
                  <button type="button" className="btn-close" onClick={handleCopyModalClose} />
                </div>
                <div className="modal-body">
                  <div className="row g-3 mb-4">
                    {copyFieldConfigs.map((field) => renderCopyField(field))}
                  </div>
                  {copyProgress.status !== 'idle' && (
                    <div
                      className={`alert ${copyProgress.status === 'error'
                        ? 'alert-danger'
                        : copyProgress.status === 'success'
                          ? 'alert-success'
                          : 'alert-info'
                        } mb-4`}
                      role="alert"
                    >
                      {copyProgress.status === 'working' && (
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                      )}
                      <span>{copyProgress.message}</span>
                      {(copyProgress.total > 0 || copyProgress.current > 0) && (
                        <div className="small text-muted mt-2">
                          {copyProgress.current} / {copyProgress.total || copySelectedTaskCount || copyPreviewTasks.length} tasks processed
                        </div>
                      )}
                    </div>
                  )}
                  <div className="text-lg fw-semibold mb-3">Tasks To Copy</div>
                  <div className="table-responsive bordered-table" style={{ maxHeight: '260px' }}>
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th style={{ width: '70px' }}>Copy</th>
                          <th>Name</th>
                          <th>Code</th>
                          <th>Type</th>
                          <th>Frequency</th>
                        </tr>
                      </thead>
                      <tbody>
                        {copyLoadingTasks ? (
                          <tr>
                            <td colSpan={5} className="text-center py-4">
                              <span
                                className="spinner-border spinner-border-sm text-primary"
                                role="status"
                                aria-hidden="true"
                              ></span>
                            </td>
                          </tr>
                        ) : copyPreviewTasks.length ? (
                          copyPreviewTasks.map((task) => (
                            <tr key={task.id}>
                              <td>
                                <div className="form-check mb-0">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={task.selected !== false}
                                    onChange={() => handleCopyTaskToggle(task.id)}
                                  />
                                </div>
                              </td>
                              <td>{task.name}</td>
                              <td>{task.code}</td>
                              <td>{task.type}</td>
                              <td>{task.frequency}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={5} className="text-center py-4 text-muted">
                              {copySelectedAssetOption
                                ? 'No tasks found on the selected asset.'
                                : 'Select an asset to preview tasks.'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {copyPreviewTasks.length > 0 && (
                    <div className="small text-muted mt-2">
                      Selected {copySelectedTaskCount} of {copyPreviewTasks.length} tasks
                    </div>
                  )}
                  {copyTaskSelectionError && (
                    <div className="text-danger small mt-2">{copyTaskSelectionError}</div>
                  )}
                  {copyResult && (
                    <div className="mt-3">
                      <span className="text-lg fw-semibold mb-3">Summary</span>
                      <ul className="mb-0 ps-3">
                        <li>Source tasks: {copyStats.sourceTasks}</li>
                        <li>Imported tasks: {copyStats.importedTasks}</li>
                        <li>Deleted tasks: {copyStats.deletedTasks}</li>
                        <li>Deleted work orders: {copyStats.deletedWorkOrders}</li>
                        {copyStats.createdAssetMeters > 0 && (
                          <li>Created asset meters: {copyStats.createdAssetMeters}</li>
                        )}
                        {copyStats.skippedWorkOrders > 0 && (
                          <li className="text-warning">
                            Skipped work orders (modified): {copyStats.skippedWorkOrders}
                          </li>
                        )}
                        {copyStats.skippedTasks > 0 && (
                          <li className="text-warning">
                            Existing tasks kept due to modified work orders: {copyStats.skippedTasks}
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={handleCopyModalClose}>
                    {copyCompleted ? 'Close' : 'Cancel'}
                  </button>
                  {!copyCompleted && (
                    <button
                      className="btn btn-primary"
                      onClick={handleCopyImport}
                      disabled={copySubmitting || !copyForm.sourceAssetId || copyLoadingTasks || !copySelectedTaskCount}
                    >
                      {copySubmitting && (
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                      )}
                      Import
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        {showTaskImporter && (
          <div className="modal show d-block mx-4" tabIndex="-1">
            <div className="modal-dialog modal-fullscreen ps-24 pe-32" style={{ height: '98%' }}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Import Tasks</h5>
                  <button type="button" className="btn-close" onClick={() => setShowTaskImporter(false)} />
                </div>
                <div className="modal-body">
                  <TaskImporter assetId={assetId} defaultCostCentre={asset.costCenter.id} onComplete={handleImportComplete} />
                </div>
              </div>
            </div>
          </div>
        )}
        {showAlignScheduleModal && (
          <div className="modal modal-backdrop show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Align Last Done to Schedule</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowAlignScheduleModal(false)}
                  />
                </div>
                <div className="modal-body">
                  <p className="mb-3">
                    This action updates the Last Done value for all selected overdue tasks so
                    they align with their defined schedule. The system will assume each task
                    was last completed at the appropriate point in its target frequency.
                  </p>
                  <p className="mb-3">
                    This is particularly useful when adding existing assets to Samurai CMMS
                    that are already partway through their service life, where maintenance
                    history is unknown or has not yet been entered. It helps prevent the
                    system from showing all tasks as overdue immediately.
                  </p>
                  <div className="mb-3">
                    <strong>Example</strong>
                  </div>
                  <p className="mb-4">
                    If an asset’s current meter reading is 41,000 hours and a task has a
                    frequency of 18,000 hours, the system will calculate the expected last
                    performed value as 36,000 hours. The Last Done value will be set to
                    36,000 hours, and the next due point will be 54,000 hours, effectively
                    pushing the task forward to its correct position in the schedule.
                  </p>
                  <div className="fw-semibold mb-2">Select the tasks to apply this change to.</div>
                  <div className="d-flex gap-2 mb-2">
                    <button
                      type="button"
                      className="btn btn-link btn-sm px-0"
                      onClick={handleAlignScheduleSelectAll}
                    >
                      Check All
                    </button>
                    <button
                      type="button"
                      className="btn btn-link btn-sm px-0"
                      onClick={handleAlignScheduleClearAll}
                    >
                      Uncheck All
                    </button>
                  </div>
                  <div className="table-responsive bordered-table" style={{ maxHeight: '320px' }}>
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Task Code</th>
                          <th>Task Name</th>
                          <th>Frequency</th>
                          <th>New Last Done</th>
                          <th className="text-end">Select</th>
                        </tr>
                      </thead>
                      <tbody>
                        {alignScheduleTasks.length ? (
                          alignScheduleTasks.map((task) => (
                            <tr key={task.id}>
                              <td>{task.taskCode?.code || task.code?.code || ''}</td>
                              <td>{task.taskCode?.name || task.code?.name || ''}</td>
                              <td>{task.frequency}</td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={alignScheduleInputs[task.id] ?? task.lastDonePerSchedule ?? ''}
                                  onChange={(event) =>
                                    handleAlignScheduleInputChange(
                                      task.id,
                                      event.target.value,
                                      task.lastDonePerSchedule,
                                    )
                                  }
                                />
                              </td>
                              <td className="text-end">
                                <div className="form-check d-flex justify-content-end mb-0">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={alignScheduleSelection.includes(task.id)}
                                    onChange={() => handleAlignScheduleToggle(task.id)}
                                  />
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={4} className="text-center text-muted py-4">
                              No tasks are eligible for alignment.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowAlignScheduleModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleAlignScheduleSave}
                    disabled={alignScheduleSaving}
                  >
                    {alignScheduleSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="table-responsive mt-3">
        <table className="table bordered-table mb-0">
          <tbody>
            <tr>
              <td>
                <div className="d-flex align-items-center justify-content-end gap-4">
                  <div className="fw-semibold">Total Life Cycle Cost</div>
                  <div>{Number(totalLcc.toFixed(0)).toLocaleString()}</div>
                </div>
              </td>
            </tr>
            {totalLccPerUnit ? (<tr>
              <td>
                <div className="d-flex align-items-center justify-content-end gap-4">
                  <div className="fw-semibold">Life Cycle Cost per {measurementUnits}</div>
                  <div>{Number(totalLccPerUnit.toFixed(2)).toLocaleString()}</div>
                </div>
              </td>
            </tr>) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
});

export default AssetTasks;
